/* Application state.
 *
 * One store, because this is one board and the screens all read the same jobs.
 * Every write inside a job goes through an action here so the assignment gate
 * (IDEA.md §5) is enforced in one place rather than re-checked per component. */
import { create } from 'zustand';
import {
  BOARD_PAGE_SIZE,
  CUSTOMERS_ALL_SIZE,
  CUSTOMERS_PAGE_SIZE,
  JOB_SORTS,
  JOBS_PAGE_SIZE,
  OFFICE_ROLES,
  PAGE_SIZES,
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULE,
  ROLE_LABEL,
  SORT_DIRECTIONS,
  colourKey,
} from '@oneshot2pac/shared';
import type {
  CustomerDto,
  CustomerRowDto,
  CustomerSort,
  CustomerType,
  DashboardDto,
  ListCustomersQuery,
  ListJobsQuery,
  MembershipStatus,
  NoticeDto,
  NoticeLevel,
  OrganisationDto,
  PersonDto,
  SessionDto,
  UpdateJobRequest,
  UpdateOrganisationRequest,
} from '@oneshot2pac/shared';
import type {
  Colour,
  DropTarget,
  Item,
  Job,
  JobSort,
  PickerKind,
  PickerRequest,
  Plan,
  Role,
  Screen,
  SettingsSection,
  SortDirection,
  Stage,
  Task,
} from '@/types';
import { ApiError } from '@/api/client';
import { authApi } from '@/api/auth';
import { coloursApi } from '@/api/colours';
import { customersApi } from '@/api/customers';
import { dashboardApi } from '@/api/dashboard';
import { jobsApi } from '@/api/jobs';
import { noticesApi } from '@/api/notices';
import { organisationApi } from '@/api/organisation';
import { peopleApi } from '@/api/people';
import { stagesApi } from '@/api/stages';
import { SHEENS, TONE_NAME } from '@/data/reference';
import {
  addDays,
  formatDay,
  mondayOf,
  nextWorkday,
  setShopTimezone,
  today,
  type IsoDay,
} from '@/domain/time';
import { duplicateWarning, findDuplicates } from '@/domain/customers';
import { invalidateArea, itemsFor, nextRowId, renumber } from '@/domain/items';
import { atRisk, hydrate, tasksFor } from '@/domain/schedule';

const THEME_KEY = 'o2p-theme';
/** Where this browser keeps the jobs list's view. Not the shop's — see
 *  `storedView`. */
const VIEW_KEY = 'o2p-jobs-view';
const TOAST_MS = 2600;

/** The ways the jobs list can be narrowed, as values rather than a type alone:
 *  a filter read back out of storage has to be checked against something. */
export const JOB_FILTERS = ['all', 'live', 'progress', 'late', 'done'] as const;

/** What the server says when you write to a job you are not on. Said here too,
 *  before the request goes out, so the floor is told by the button rather than
 *  by a round trip on bad wifi. */
const NOT_ON_IT = 'You’re not on this job — ask the office to put you on it.';

const OFFLINE_JOBS = 'Could not load the jobs. Check the wifi and try again.';

/** How long to wait after a keystroke before asking the server for a page.
 *  Every letter typed is otherwise a query, and shop wifi is the reason this
 *  number exists. */
const SEARCH_MS = 250;

let searchTimer: ReturnType<typeof setTimeout> | undefined;
let custSearchTimer: ReturnType<typeof setTimeout> | undefined;

/** Which jobs-list request is the current one.
 *
 *  Typing "hen" is three requests, and on shop wifi they can come back in any
 *  order — so an answer that is not the latest one asked for is dropped rather
 *  than drawn. Without this, the list settles on whichever request happened to
 *  be slowest, which reads as the search box ignoring the last letter typed. */
let listRequest = 0;

/** The same, for the customers list — its own counter, because the two lists
 *  are asked separately and an answer to one says nothing about the other. */
let custRequest = 0;

/** Board stages are the admin's (IDEA.md §8), so every write to them says the
 *  same thing when someone else tries. */
const OFFICE_ONLY = 'Only the owner and the office change the board’s stages.';

/** The same rule for the notice board: putting something in front of the whole
 *  shop is office work, the way the stages and the people list are. */
const OFFICE_ONLY_NOTICES = 'Only the owner and the office put notices up.';

/** And for the colours: reference data is the office's (IDEA.md §8). */
const OFFICE_ONLY_COLOURS = 'Only the owner and the office change the colour list.';

/** The card currently under the pointer during a drag. Deliberately outside the
 *  store: it changes on every dragover and nothing renders from it directly. */
let dragging: string | null = null;
/** The stage row being dragged in Settings. Same reasoning as `dragging`. */
let draggingStage: string | null = null;
let toastTimer: ReturnType<typeof setTimeout> | undefined;

export interface JobDraft {
  customer: string;
  title: string;
  ref: string;
  site: string;
  /** The four dates are `IsoDay`s — the same strings the server stores and the
   *  same ones `<input type="date">` reads and writes. They used to be the
   *  shop's own labels ("Thu 3 Sep"), parsed back on the way out, which meant a
   *  date existed as text in the form and as a number on the board and only one
   *  of those could be wrong at a time. Null is "not set", which is a real
   *  answer on a job being taken in. */
  received: IsoDay | null;
  colour: string;
  sheen: number;
  profile: string;
  items: number;
  estStart: IsoDay | null;
  estDays: number;
  deadline: IsoDay | null;
}

/** The job header, as the office corrects it after the job is taken in.
 *
 *  Everything on the header that is typed rather than picked off the board:
 *  what it is, whose reference it carries, where it goes back to, and the four
 *  dates. The colour and the sheen are not here — they have the swatch's own
 *  Change, which writes straight through, and the item lines follow a colour
 *  when it moves.
 *
 *  It is a draft, like the customer and person forms, not a set of fields
 *  typed into the job: a job whose title is half retyped must not be on the
 *  board under half a title, and Cancel has to mean the job is as it was.
 *  Dates are the shop's own labels — "Thu 3 Sep" — and become ISO days on the
 *  way out. */
export interface JobEdit {
  title: string;
  ref: string;
  site: string;
  received: IsoDay | null;
  promised: IsoDay | null;
  estStart: IsoDay | null;
  estDays: number;
  deadline: IsoDay | null;
}

/** One field of it, so a fact on the job sheet can open the form on itself. */
export type JobEditField = keyof JobEdit;

/** The customer form, which both adds and edits. Sites are one per line: a
 *  textarea adds, corrects and removes an address without three separate
 *  controls, and it is the same field whether the customer is new or already
 *  has four sites. */
export interface CustomerDraft {
  name: string;
  type: CustomerType;
  contact: string;
  phone: string;
  email: string;
  ref: string;
  sites: string;
  note: string;
}

const EMPTY_CUSTOMER: CustomerDraft = {
  name: '',
  type: 'Trade',
  contact: '',
  phone: '',
  email: '',
  ref: '',
  sites: '',
  note: '',
};

/** The shop's own details, as Settings → Organisation types them in — the four
 *  real columns on the organisation, held as text because that is what an input
 *  gives back. The ABN is the one that may be blank; blank clears it. */
export interface OrgDraft {
  name: string;
  abn: string;
  timezone: string;
  jobCodePrefix: string;
}

/** Until the session lands there is no shop, so the form is empty rather than
 *  showing somebody else's shop for a frame. */
const EMPTY_ORG: OrgDraft = { name: '', abn: '', timezone: '', jobCodePrefix: '' };

/** The shop as the form reads it. Null is "not on the paperwork", which an
 *  input has no way to hold — it types as blank, and saves back as null. */
const orgFrom = (org: OrganisationDto): OrgDraft => ({
  name: org.name,
  abn: org.abn ?? '',
  timezone: org.timezone,
  jobCodePrefix: org.jobCodePrefix,
});

/** A new person, as the office types them in. The password is handed over in
 *  the shop — there is no mailer yet, and no invitation link to send. */
export interface PersonDraft {
  name: string;
  email: string;
  password: string;
  role: Role;
  financialAccess: boolean;
}

const EMPTY_PERSON: PersonDraft = {
  name: '',
  email: '',
  password: '',
  role: 'staff',
  financialAccess: false,
};

/** The notice form, which both puts one up and corrects one already up.
 *
 *  A draft like the customer and person forms, so Cancel means the board is as
 *  it was: a notice half retyped must not be up in front of the whole shop
 *  under half a sentence. */
export interface NoticeDraft {
  title: string;
  body: string;
  level: NoticeLevel;
}

const EMPTY_NOTICE: NoticeDraft = {
  title: '',
  body: '',
  // Ordinary unless somebody says otherwise. A board where the default is
  // urgent is a board where nothing is.
  level: 'notice',
};

/** The stage form. The WIP limit is held as typed text so the field can be
 *  emptied — blank and 0 both mean no limit, and neither may become NaN. */
export interface StageDraft {
  name: string;
  tone: string;
  wip: string;
  def: boolean;
  terminal: boolean;
}

const EMPTY_STAGE: StageDraft = {
  name: '',
  tone: '',
  wip: '',
  def: false,
  terminal: false,
};

/** The colour form, which both adds and edits. The hex is what the swatch
 *  picker hands back — `#RRGGBB` — and it is typed as well, because a fan deck
 *  lists one and matching it by eye in a colour wheel is not a job for gloves. */
export interface ColourDraft {
  brand: string;
  code: string;
  name: string;
  hex: string;
}

const EMPTY_COLOUR: ColourDraft = {
  brand: '',
  code: '',
  name: '',
  // White: most of what this shop sprays is one, and a chip that opens black
  // reads as a mistake.
  hex: '#FFFFFF',
};

/** A navigation held up because something on the screen isn't saved: what
 *  would be lost, said in a sentence, and the move waiting on the answer. */
export interface Leaving {
  what: string;
  run: () => void;
}

/** Where a list that comes from the server has got to. */
export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

/** The three long lists on the job sheet, one open at a time. */
export type JobTab = 'items' | 'tasks' | 'photos';

/** How the unscheduled panel is ordered. Three questions the office actually
 *  asks of a job nobody has promised a date yet: what came in first, what is
 *  owed back soonest, and what would fit in a gap. */
export type RailSort = 'in' | 'deadline' | 'days';

/** Where the app is with the session. `checking` is the boot state: the app
 *  renders nothing but a holding screen until the session call answers, so a
 *  signed-in person never sees the sign-in form flash past. */
export type AuthStatus = 'checking' | 'signed-out' | 'signed-in';

interface AppState {
  screen: Screen;
  /** Where a job or customer detail screen was opened from, for the back link. */
  origin: Screen;
  jobCode: string;
  theme: 'light' | 'dark';

  authStatus: AuthStatus;
  /** Who is signed in, from the server. Null until the session call answers. */
  session: SessionDto | null;
  /** Why the last sign-in attempt failed, in the server's words. */
  authError: string;
  /** Why someone is looking at the sign-in screen when nothing went wrong —
   *  they changed their own password, so every session went with it. */
  authNotice: string;
  signingIn: boolean;

  /** The role the interface is being rendered for.
   *
   *  It starts as the signed-in person's real role from their membership. The
   *  account menu lets the office preview the other roles, because the
   *  permission rules in IDEA.md §5 are much easier to check than to describe.
   *  Previewing changes what this build draws, not what the server allows —
   *  when jobs move to the database in slice two, the server is the only thing
   *  that decides, and this becomes a view filter only. */
  role: Role;

  /** The board's working set: every job the signed-in person can see, in board
   *  order. A kanban cannot be paged — a column missing its last three cards is
   *  a board nobody trusts — so this is one page big enough to be all of it.
   *
   *  The jobs list screen does not read this. It asks the same endpoint for a
   *  page of its own, sorted and filtered by the server, because that list is
   *  every job the shop has ever done and the archive only grows. */
  jobs: Job[];
  jobsStatus: LoadStatus;
  jobsError: string;

  /** The jobs list screen's page, and what was asked for to get it. The sort
   *  and the page live here rather than in the screen because they are part of
   *  the request: changing either is a new question for the server.
   *
   *  Living here is also what keeps them across a navigation — the screen is
   *  unmounted every time somebody opens a job, and a filter that unmounts
   *  with it is a filter nobody sets twice. The six fields that make up the
   *  question (`jobFilter`, `jobSort`, `jobDir`, `listPage`, `listSize`, `q`)
   *  are mirrored into this browser's storage as well, so a reload comes back
   *  to the same question — see `storedView`. */
  list: Job[];
  listStatus: LoadStatus;
  listError: string;
  listPage: number;
  listPages: number;
  /** How many rows a page holds. The office sets it — an archive is scanned a
   *  hundred at a time, an iPad wants a page that fits — and the server is
   *  what it is asked of, so this is part of the request, not a view setting. */
  listSize: number;
  /** Jobs matching the filter and the search — what the pager counts. */
  listTotal: number;
  /** Every job the shop has that this person can see, filtered or not. */
  listVisible: number;
  jobSort: JobSort;
  jobDir: SortDirection;

  /** The board's stages, in board order — the admin's list, from the server,
   *  edited under Settings → Board stages. The board, the move sheet, the jobs
   *  list and every stage label read this and nothing else, so a rename, a
   *  reorder or a new stage lands everywhere at once.
   *
   *  Loaded with the session and awaited with the jobs: a board drawn before
   *  its columns arrive is a board with every job standing nowhere. */
  stages: Stage[];

  /** The stage form, which both adds and edits. `stageEditId` is the stage
   *  being changed, null when adding. */
  stageOpen: boolean;
  stageEditId: string | null;
  stageDraft: StageDraft;
  stageError: string;
  /** True while the form is on its way to the server, so Save cannot be
   *  pressed twice. */
  savingStage: boolean;

  /** The shop's colours, from the server — live ones first, most used first,
   *  then the archived ones. Every chip on every card is drawn from this, so
   *  it comes with the session like the stages; the pickers offer only the
   *  live ones (`liveColours`). */
  colours: Colour[];

  /** The colour form, which both adds and edits. `colourEditId` is the colour
   *  being changed, null when adding. */
  colourOpen: boolean;
  colourEditId: string | null;
  colourDraft: ColourDraft;
  colourError: string;
  /** True while a colour write is on its way — the form's Save, or a row's
   *  Archive or Bring back — so none of them can be pressed twice. */
  savingColour: boolean;

  /** Settings → Organisation: what is on screen, and what the last save left.
   *  `unsaved` compares the two, so an ABN typed and typed back is not unsaved
   *  work. Both are filled from the session on the way in. */
  org: OrgDraft;
  savedOrg: OrgDraft;
  /** True while the shop's details are on their way to the server, so the Save
   *  cannot be pressed twice. */
  orgSaving: boolean;
  /** Why the last save was refused — the server's sentence, kept under the
   *  form rather than in a toast that goes away while it is being read. */
  orgError: string;

  /** The shop's customers, from the server — real records, unlike the jobs
   *  still hanging off them. Loaded on sign-in rather than when the customers
   *  screen opens, because the new-job form needs them too.
   *
   *  A job still names its customer rather than pointing at one; the name is
   *  unique within a shop, which is what makes that hold until jobs move to the
   *  database as well. */
  custs: CustomerDto[];
  custStatus: LoadStatus;
  custsError: string;
  /** Which customer the customer page is showing, by name. */
  custName: string;

  /** The customers screen's page, and what was asked for to get it.
   *
   *  The same arrangement the jobs list has, for the same reason: the list is
   *  every customer the shop has ever sprayed for and it only grows, so the
   *  search, the ordering and the page are one question the server answers.
   *  The heading you click does not reorder the rows on screen — it asks
   *  again, because ordering a page of 25 would quietly leave the other 300
   *  out of it, which looks right and is wrong.
   *
   *  Not mirrored into this browser's storage the way the jobs view is: a
   *  customer list is scanned and left, not narrowed and returned to. */
  custList: CustomerRowDto[];
  custListStatus: LoadStatus;
  custListError: string;
  custPage: number;
  custPages: number;
  custSize: number;
  /** Customers matching the search — what the pager counts. */
  custTotal: number;
  /** Every customer the shop has, searched or not. */
  custVisible: number;
  custSort: CustomerSort;
  custDir: SortDirection;
  /** The customer list's search box. */
  custQ: string;

  /** The customer form, which both adds and edits. `custEditId` is the customer
   *  being changed, null when adding. */
  custOpen: boolean;
  custEditId: string | null;
  custDraft: CustomerDraft;
  custError: string;
  savingCust: boolean;

  /** The dashboard, as the server answered it for whoever is signed in.
   *
   *  One record rather than a field per figure: it is one request, and every
   *  number on it is counted against the same day and the same stage list. Half
   *  a dashboard from this morning beside half from now would be a screen that
   *  does not add up. Null until it has been asked for — the screen loads it
   *  when it opens, the way the two paged lists do.
   *
   *  `shop` inside it is null for anyone but the office. That is the server's
   *  answer, not a flag read here. */
  dash: DashboardDto | null;
  dashStatus: LoadStatus;
  dashError: string;

  /** The shop's notice board, and how much of it is new to whoever is signed
   *  in.
   *
   *  Loaded on the way in rather than when the dashboard opens, because the
   *  count rides on the Dashboard tab from every screen — a badge that only
   *  appears once you have been to the screen it is pointing at is a badge that
   *  has missed the point. */
  notices: NoticeDto[];
  unread: number;
  noticesStatus: LoadStatus;
  noticesError: string;

  /** The notice form, which both puts one up and corrects one already up.
   *  `noticeEditId` is the notice being changed, null when writing a new one. */
  noticeOpen: boolean;
  noticeEditId: string | null;
  noticeDraft: NoticeDraft;
  noticeError: string;
  savingNotice: boolean;

  /** The shop's people list, from the server — the one list in the app that is
   *  already real. Loaded when Settings → People opens rather than on boot,
   *  because most sessions never look at it. */
  people: PersonDto[];
  peopleStatus: LoadStatus;
  peopleError: string;

  /** The person form, which both adds and edits. Adding creates the account
   *  and the office hands the password over in person; invitation email comes
   *  later. `personEditId` is the membership being edited, null when adding. */
  personOpen: boolean;
  personEditId: string | null;
  personDraft: PersonDraft;
  personError: string;
  savingPerson: boolean;

  /** Setting somebody else's password: the membership being reset, and the
   *  password being typed. The office reads it out; nothing is emailed. */
  resetId: string | null;
  resetDraft: string;
  resetError: string;
  savingReset: boolean;

  /** Changing your own, from the account menu. This one asks for the password
   *  you already have — an account left signed in on a bench iPad must not be
   *  a way to take it over. */
  pwOpen: boolean;
  pwCurrent: string;
  pwNext: string;
  pwError: string;
  savingPw: boolean;

  /** The item editor's working set for the open job — saved back explicitly. */
  rows: Item[];
  sel: number[];
  /** What the last save left behind, for each of the three forms on a page
   *  that carry their own Save button. Unsaved work is this compared against
   *  what is on screen, never a flag: a flag set by the first keystroke says a
   *  change typed and typed back is still unsaved, and it isn't. */
  savedRows: Item[];
  /** The line just added, duplicated or pasted in — the item editor scrolls to
   *  it and flashes it once, then clears this. Null the rest of the time. */
  justAdded: number | null;
  tasks: Task[];
  note: string;
  savedNote: string;

  /** Which of the job sheet's three long lists is open. The header, the people
   *  and the stage history stay on screen; items, tasks and photos take turns,
   *  because all three stacked is taller than the iPad they are read on. */
  jobTab: JobTab;

  /** Whether the job sheet's header is showing its detail — the colour, the
   *  spec line, the dates and the actions beside them. Collapsed, the header
   *  keeps the job code, the progress track and the move action, and gives
   *  the room back to the lists below it: what someone reading the items or
   *  the stage history actually wants the screen for. Held for the session
   *  rather than per job, because it is how the shop is reading this
   *  afternoon, not a fact about J-1042. */
  jobHeadOpen: boolean;

  /** How wide the board draws its columns. `full` is the canvas card at its
   *  designed width and the board scrolls sideways; `fit` squeezes every stage
   *  onto the screen with a compact card. */
  boardMode: 'full' | 'fit';
  /** The one stage column drawn open, or null for none. Open, a column takes a
   *  width the whole card reads at and its jobs drop the short card; the rest
   *  of the board stays short. It only means anything where the columns are
   *  too narrow for the whole card in the first place — an iPad — and like
   *  `boardMode` it is how someone is reading the shop this afternoon, not a
   *  fact about the stage, so it is state and never reaches the server. */
  boardOpen: string | null;

  /** The schedule window: how many days it draws, and the day it opens on.
   *  `rangeFrom` is always a Monday — the schedule pages a week at a time. */
  range: number;
  rangeFrom: IsoDay;

  /** The unscheduled panel: whether it is open, and what it is ordered by. It
   *  costs the grid a quarter of the screen, so it opens on a button rather
   *  than sitting there — and closed is the default, because the question the
   *  schedule answers first is what is already booked in. */
  railOpen: boolean;
  railSort: RailSort;
  q: string;
  jobFilter: (typeof JOB_FILTERS)[number];
  settings: SettingsSection;

  menuOpen: boolean;
  moveOpen: boolean;
  newOpen: boolean;
  /** The job header form, open on a job. Null the rest of the time. */
  jobEdit: JobEdit | null;
  /** Which fact was clicked to open it, so that field takes the focus. Null
   *  when the form was opened from the header's own Edit details. */
  jobEditFocus: JobEditField | null;
  picker: PickerRequest | null;
  dropAt: DropTarget | null;
  toast: string;

  /** The form the open dialog opened with, as JSON. One dialog is open at a
   *  time, so one field covers all of them — and it is what tells a customer
   *  half typed from one nobody touched, which is the difference between
   *  losing work and closing an empty form. */
  dialogBase: string;

  /** A move waiting on "Keep editing" or "Discard changes". Null the rest of
   *  the time, which is nearly always. */
  leaving: Leaving | null;

  draft: JobDraft;
}

interface AppActions {
  flash: (msg: string) => void;
  setTheme: (theme: 'light' | 'dark') => void;

  /** Boot: ask the server who we are. Runs once, before anything renders. */
  loadSession: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  go: (screen: Screen) => void;
  openJob: (code: string) => void;
  openCustomer: (name: string) => void;
  goBack: () => void;

  /** Every way out of a screen goes through here. If nothing is waiting to be
   *  saved the move happens now; otherwise it is held and the shop is asked
   *  first. The navigating actions call it on themselves, so a move cannot get
   *  past the check by taking a different route. */
  leave: (run: () => void) => void;
  /** Keep editing — the held move is dropped. */
  stay: () => void;
  /** Discard the changes and go. The page forms go back to what their last
   *  save left, so the warning was true and nothing dirty follows you. */
  discard: () => void;

  /** The board's working set. Awaited on the way in, because every landing
   *  screen is jobs and a deep link to one has to find it there. */
  loadJobs: () => Promise<void>;
  /** One page of the jobs list, as the sort, filter, search and page say. */
  loadJobList: () => Promise<void>;
  /** Click a column heading: sort by it, or turn it around if it is already
   *  the sorted one. */
  sortJobsBy: (column: JobSort) => void;
  goToJobPage: (page: number) => void;
  /** Change how many rows a page holds. Back to page 1, because page 4 of 25
   *  is not page 4 of 100 and landing there reads as jobs having gone. */
  setJobPageSize: (size: number) => void;
  /** The list's search box and its filter. Both put the office back on page 1 —
   *  staying on page 4 of a list that just became one page long is how a search
   *  comes back empty when it matched. */
  setJobQuery: (q: string) => void;
  setJobFilter: (filter: AppState['jobFilter']) => void;

  move: (stageId: string) => void;
  tryMove: (code: string, stageId: string) => void;
  beginDrag: (code: string) => void;
  /** A card dropped on the column background lands at the end of that stage. */
  dropOnColumn: (stageId: string) => void;
  endDrag: () => void;
  hoverCard: (code: string, before: boolean) => void;
  dropOnCard: (targetCode: string) => void;
  nudge: (code: string, dir: -1 | 1) => void;

  setRow: (id: number, patch: Partial<Item>) => void;
  clearJustAdded: () => void;
  addRow: () => void;
  duplicateRow: (id: number) => void;
  duplicateLast: () => void;
  deleteRow: (id: number) => void;
  toggleSelected: (id: number) => void;
  clearSelection: () => void;
  deleteSelected: () => void;
  saveItems: () => Promise<void>;

  toggleTask: (index: number) => void;
  setNote: (note: string) => void;
  saveNote: () => Promise<void>;
  setOrg: (patch: Partial<OrgDraft>) => void;
  saveOrg: () => Promise<void>;

  openPicker: (kind: PickerKind, rowId: number | null, stageId?: string) => void;
  closePicker: () => void;
  applyToRows: (patch: Partial<Item>) => void;
  setStageTone: (stageId: string, hex: string) => void;
  setJobColour: (code: string) => Promise<void>;

  /** Board stages. Adding, editing, reordering and archiving all live here so
   *  the rules that keep a board coherent — one default, a name nothing else
   *  uses, no archiving a stage with jobs still in it — are said in one place
   *  before the request goes out. The stage service on the server holds the
   *  same rules, and its answer is the one that counts. */
  loadStages: () => Promise<void>;
  openAddStage: () => void;
  openEditStage: (stage: Stage) => void;
  setStageDraft: (patch: Partial<StageDraft>) => void;
  saveStage: () => Promise<void>;
  archiveStage: () => Promise<void>;
  closeStage: () => void;
  moveStage: (id: string, dir: -1 | 1) => void;
  beginStageDrag: (id: string) => void;
  dropOnStage: (id: string) => void;
  endStageDrag: () => void;

  /** Colours. Adding and editing wait on the server, like the stage form;
   *  archiving and bringing back are row actions, and the server's list
   *  replaces this one each time. */
  loadColours: () => Promise<void>;
  openAddColour: () => void;
  openEditColour: (colour: Colour) => void;
  setColourDraft: (patch: Partial<ColourDraft>) => void;
  saveColour: () => Promise<void>;
  archiveColour: (colour: Colour) => Promise<void>;
  restoreColour: (colour: Colour) => Promise<void>;
  closeColour: () => void;

  /** Open the job header form. Given a field, it opens on that one — which is
   *  what a fact on the job sheet clicked in place does. */
  openJobEdit: (focus?: JobEditField) => void;
  setJobEdit: (patch: Partial<JobEdit>) => void;
  saveJob: () => Promise<void>;

  /** Opens the new-job form. Given a customer, it starts on them — that is
   *  the New job on a customer's page. */
  openNewJob: (customer?: CustomerDto) => void;
  setDraft: (patch: Partial<JobDraft>) => void;
  createJob: () => Promise<void>;
  /** Every customer the shop has: what the job form's picker offers and what
   *  the duplicate check compares against. One page big enough to be all of
   *  them, the way the board takes the jobs. */
  loadCustomers: () => Promise<void>;
  /** One page of the customers screen, as its search, ordering and page say. */
  loadCustomerList: () => Promise<void>;
  /** Click a column heading: order by it, or turn it around if it is already
   *  the one the list is in order of. */
  sortCustomersBy: (column: CustomerSort) => void;
  goToCustomerPage: (page: number) => void;
  setCustomerPageSize: (size: number) => void;
  /** The customer list's search box. Back to page 1 — staying on page 4 of a
   *  list that just became one page long is how a search comes back empty when
   *  it matched. */
  setCustomerQuery: (q: string) => void;
  /** Add a delivery site to a customer, from the new-job form. */
  addSite: (customer: CustomerDto, site: string) => Promise<void>;
  openNewCustomer: () => void;
  openEditCustomer: (customer: CustomerDto) => void;
  setCustDraft: (patch: Partial<CustomerDraft>) => void;
  saveCustomer: () => Promise<void>;

  loadDashboard: () => Promise<void>;
  loadNotices: () => Promise<void>;
  markNoticesRead: () => Promise<void>;
  openNewNotice: () => void;
  openEditNotice: (notice: NoticeDto) => void;
  setNoticeDraft: (patch: Partial<NoticeDraft>) => void;
  saveNotice: () => Promise<void>;
  takeNoticeDown: (id: string) => Promise<void>;

  loadPeople: () => Promise<void>;
  openAddPerson: () => void;
  openEditPerson: (person: PersonDto) => void;
  setPersonDraft: (patch: Partial<PersonDraft>) => void;
  savePerson: () => Promise<void>;
  setPersonStatus: (status: MembershipStatus) => Promise<void>;
  openResetPassword: (person: PersonDto) => void;
  setResetPassword: (value: string) => void;
  resetPassword: () => Promise<void>;

  openChangePassword: () => void;
  setPassword: (patch: { current?: string; next?: string }) => void;
  changePassword: () => Promise<void>;

  set: <K extends keyof AppState>(key: K, value: AppState[K]) => void;
}

export type App = AppState & AppActions;

/** The job the detail screens are showing. Falls back to the first job so a
 *  stale code can never blank the screen — and is undefined when there are no
 *  jobs at all, which is a real state: a shop on its first morning, and the
 *  moment before the board has answered. Every caller draws it. */
export const currentJob = (s: AppState): Job | undefined =>
  s.jobs.find((j) => j.code === s.jobCode) ?? s.jobs[0];

/** Editing a job requires being assigned to it — except owner and admin, who
 *  run the office and must be able to fix jobs they aren't on (IDEA.md §5).
 *
 *  Assignment is not modelled on the server yet, so this is the role half of
 *  the rule. The other half arrives with jobs in slice two, and it arrives in
 *  the job service — never re-checked per component. */
export const canEdit = (s: AppState): boolean => (OFFICE_ROLES as readonly Role[]).includes(s.role);

/** Owner and admin run the office: settings, the people list, and any job.
 *  Separate from `canEdit` on purpose — that one grows an assignment check in
 *  slice two, and this one never does. */
export const isOffice = (s: AppState): boolean =>
  (OFFICE_ROLES as readonly Role[]).includes(s.role);

/** The customer the customer page is showing, if the list has loaded and still
 *  has them. Undefined is a real state — a shop with no customers yet, or a
 *  list that hasn't arrived — and the screen draws it rather than falling back
 *  to somebody else's contact details. */
export const currentCustomer = (s: AppState): CustomerDto | undefined =>
  s.custs.find((c) => c.name === s.custName) ?? s.custs[0];

/** Subcontractors don't see customer detail. */
export const seesCustomers = (s: AppState): boolean => s.role !== 'subcontractor';

/** Costs, rates, quotes and invoices — a flag on the membership, independent of
 *  role. Nothing reads it yet; money is out of scope until Phase 3. */
export const seesMoney = (s: AppState): boolean => s.session?.membership.financialAccess ?? false;

/** One stage off the admin's list. Undefined is a real state — a job can sit at
 *  a stage that has since been archived — so every caller draws that case. */
export const stageOf = (s: AppState, id: string): Stage | undefined =>
  s.stages.find((x) => x.id === id);

/** A stage's name for a label. Falls back to the id rather than blank: a job at
 *  an archived stage should say something you can search for, not nothing. */
export const stageName = (s: AppState, id: string): string => stageOf(s, id)?.name ?? id;

/** The tint a stage's column carries. Empty means no tint — the stage name is
 *  always drawn as well, so colour is never carrying the meaning on its own. */
export const toneOf = (s: AppState, id: string): string => stageOf(s, id)?.tone ?? '';

/** A colour off the shop's list, by the code a job or an item holds — archived
 *  ones included, because a job sprayed in one still draws its chip. Undefined
 *  is a real state: the moment before the list has answered. */
export const colourOf = (s: AppState, code: string): Colour | undefined =>
  s.colours.find((c) => c.code === code);

/** The same, for a screen that has to draw *something*: a code the list does
 *  not have still reads as its code, with no swatch, rather than taking the
 *  card down with it. */
export const colourFor = (s: AppState, code: string): Colour =>
  colourOf(s, code) ?? {
    id: '',
    brand: '',
    code,
    name: 'Not on the colour list',
    hex: '',
    jobs: 0,
    archived: false,
  };

/** The colours a picker offers: the ones the office has not archived. */
export const liveColours = (s: AppState): Colour[] => s.colours.filter((c) => !c.archived);

/** The colour already on the list with this code, if there is one — the same
 *  match the server refuses a write on, so the form can say it first. `self`
 *  is the colour being edited, which may keep its own code. */
export const colourWithCode = (
  s: AppState,
  code: string,
  self: string | null,
): Colour | undefined => {
  const key = colourKey(code);
  return key ? s.colours.find((c) => c.id !== self && colourKey(c.code) === key) : undefined;
};

/** Two forms compared. They are plain data built from the same shapes, so the
 *  key order is stable and this is an honest deep compare. */
const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** A dialog's form against the snapshot it opened with. */
const untouched = (form: unknown, base: string): boolean => JSON.stringify(form) === base;

/** What has been typed and not saved, said in a sentence naming it — null when
 *  there is nothing to lose, which is nearly always.
 *
 *  Unsaved means one thing here: a form with its own Save button that hasn't
 *  been pressed. A stage move, a ticked task and a dragged card are actions,
 *  not forms — they read as done the moment they happen, and warning about
 *  them would teach the shop to click through the warning. */
export const unsaved = (s: AppState): string | null => {
  // A dialog is in front of everything else and closing it is the only way out
  // of it, so it answers for itself. What is behind it isn't going anywhere.
  if (s.newOpen) {
    return untouched(s.draft, s.dialogBase) ? null : 'This job has not been created yet.';
  }
  if (s.custOpen) {
    return untouched(s.custDraft, s.dialogBase) ? null : 'This customer is not saved.';
  }
  if (s.personOpen) {
    return untouched(s.personDraft, s.dialogBase) ? null : 'This person is not saved.';
  }
  if (s.stageOpen) {
    return untouched(s.stageDraft, s.dialogBase) ? null : 'This stage is not saved.';
  }
  if (s.colourOpen) {
    return untouched(s.colourDraft, s.dialogBase) ? null : 'This colour is not saved.';
  }
  if (s.noticeOpen) {
    return untouched(s.noticeDraft, s.dialogBase) ? null : 'This notice is not up yet.';
  }
  if (s.jobEdit) {
    return untouched(s.jobEdit, s.dialogBase) ? null : `The changes to ${s.jobCode} are not saved.`;
  }
  if (s.resetId) return s.resetDraft === '' ? null : 'The password you typed has not been set.';
  if (s.pwOpen) {
    return s.pwCurrent === '' && s.pwNext === ''
      ? null
      : 'The password you typed has not been saved.';
  }

  // The job and its item editor are one place: the job sheet's Items tab shows
  // the same working set, so moving between them loses nothing.
  if (s.screen === 'job' || s.screen === 'items') {
    const rows = !same(s.rows, s.savedRows);
    const note = s.note !== s.savedNote;
    if (rows && note) return `Item lines and a note on ${s.jobCode} are not saved.`;
    if (rows) return `Item lines on ${s.jobCode} are not saved.`;
    if (note) return `A note on ${s.jobCode} is not saved.`;
    return null;
  }

  if (s.screen === 'settings' && s.settings === 'org' && !same(s.org, s.savedOrg)) {
    return 'The organisation settings are not saved.';
  }

  return null;
};

/** Where a new job lands. The list always has a default; if the flag has gone
 *  missing the first stage is the honest answer. */
export const defaultStage = (s: AppState): Stage | undefined =>
  s.stages.find((x) => x.def) ?? s.stages[0];

/** What the jobs list is asking for, as a query the server can answer.
 *
 *  Two of these fields exist because the stage list and the colours were the
 *  client's when the list went to the server: a job stores a stage *id* and a
 *  colour *code*, so what "in the shop" means, and which colours "Vivid White"
 *  matches, are resolved here and sent as ids. Both are rows on the server
 *  now, so both halves can move there. Archived colours are searched too —
 *  a job sprayed in one is still in it. */
const listQuery = (st: AppState): ListJobsQuery => {
  const q = st.q.trim();
  const lower = q.toLowerCase();
  const idsOf = (keep: (stage: Stage) => boolean) => st.stages.filter(keep).map((x) => x.id);
  const inTheShop = idsOf((x) => !x.terminal);

  const filters: Record<AppState['jobFilter'], { stages?: string[]; overdue?: boolean }> = {
    all: {},
    live: { stages: inTheShop },
    // Work the shop has actually started: past the stage new jobs land in,
    // short of a terminal one. Read off the flags, so a renamed or inserted
    // stage needs no change here.
    progress: { stages: idsOf((x) => !x.def && !x.terminal) },
    late: { stages: inTheShop, overdue: true },
    done: { stages: idsOf((x) => Boolean(x.terminal)) },
  };

  return {
    page: st.listPage,
    pageSize: st.listSize,
    sort: st.jobSort,
    dir: st.jobDir,
    ...filters[st.jobFilter],
    q: q || undefined,
    qStages: q ? idsOf((x) => x.name.toLowerCase().includes(lower)) : undefined,
    qColours: q
      ? st.colours
          .filter((c) => `${c.brand} ${c.name}`.toLowerCase().includes(lower))
          .map((c) => c.code)
      : undefined,
  };
};

/** What the customers list is asking for, as a query the server can answer.
 *
 *  `liveStages` is here for the same reason `listQuery`'s stage ids are: "in
 *  the shop" is a flag on the stage list, and the server counts a customer's
 *  jobs and finds their next promise over the stages it is handed — so the
 *  flags are resolved here and sent as ids, until the server reads them. */
const custQuery = (st: AppState): ListCustomersQuery => ({
  page: st.custPage,
  pageSize: st.custSize,
  sort: st.custSort,
  dir: st.custDir,
  q: st.custQ.trim() || undefined,
  liveStages: st.stages.filter((x) => !x.terminal).map((x) => x.id),
});

/** Put a card at `index` within a stage, and renumber that stage.
 *
 *  The same arithmetic the server does, done here first so the board answers a
 *  drag on the frame it happened rather than a round trip later. The server is
 *  still the authority — its answer replaces this — but on shop wifi the
 *  difference between the two is the difference between a board that feels
 *  broken and one that doesn't. */
const placeCard = (jobs: Job[], code: string, stageId: string, index: number): Job[] => {
  const card = jobs.find((j) => j.code === code);
  if (!card) return jobs;
  const rest = jobs.filter((j) => j.code !== code);
  const column = rest.filter((j) => j.stage === stageId);
  column.splice(Math.max(0, Math.min(index, column.length)), 0, { ...card, stage: stageId });
  return rest
    .filter((j) => j.stage !== stageId)
    .concat(column.map((j, i) => ({ ...j, boardOrder: i })));
};

/** The server's version of one stage, dropped over ours. */
const takeColumn = (jobs: Job[], stageId: string, column: Job[]): Job[] => {
  const moved = new Set(column.map((j) => j.code));
  return jobs.filter((j) => j.stage !== stageId && !moved.has(j.code)).concat(column);
};

/** Change a job, and take the server's answer for what it now is.
 *
 *  The screen has already moved on when this runs. A saved note, a recoloured
 *  job and a saved item count all read as done the moment they happen, and a
 *  spinner over each of them would cost every save to guard against a refusal
 *  that is rare. So the write goes behind them — and when it is refused, the
 *  board is reloaded rather than unwound by hand, and the shop is told in the
 *  server's words. What was typed stays on screen; it goes back to being
 *  unsaved, which is what it is. */
/** What to put under a form the server refused.
 *
 *  A failed validation carries a sentence per field — "An ABN is eleven
 *  digits" — under a summary that says only that something needs fixing. The
 *  field's own sentence is the one worth reading, so it wins when there is
 *  one. */
const refusalOf = (err: unknown): string => {
  if (!(err instanceof ApiError)) {
    return 'Could not reach the server. Check the wifi and try again — nothing has been lost.';
  }
  return Object.values(err.issues)[0] ?? err.message;
};

async function writeJob(
  get: () => App,
  set: (partial: Partial<App>) => void,
  code: string,
  patch: UpdateJobRequest,
): Promise<void> {
  try {
    const saved = hydrate(await jobsApi.update(code, patch));
    set({ jobs: get().jobs.map((j) => (j.code === code ? saved : j)) });
  } catch (err) {
    await get().loadJobs();
    if (get().jobCode === code) {
      const held = get().jobs.find((j) => j.code === code);
      set({ savedNote: held?.note ?? '' });
    }
    get().flash(
      err instanceof ApiError
        ? err.message
        : `Could not save ${code}. Check the wifi and try again.`,
    );
  }
}

/** Move a card, and mean it: on the board now, in the database next.
 *
 *  Every board move goes through here — the move sheet, a card dropped on a
 *  column, a card dropped on another card, and the reorder nudges — so the
 *  optimistic half and the request are written once. A refusal puts the board
 *  back from the server rather than by undoing by hand: whatever the server
 *  says the board is, is what it is. */
async function placeOnBoard(
  get: () => App,
  set: (partial: Partial<App>) => void,
  code: string,
  stageId: string,
  index: number,
): Promise<void> {
  // The card it lands above, read before the move is applied and with the
  // dragged card taken out — the server places it the same way.
  const before = get().jobs.filter((j) => j.stage === stageId && j.code !== code)[index]?.code;

  set({ jobs: placeCard(get().jobs, code, stageId, index), dropAt: null });

  try {
    const answer = await jobsApi.move(code, { stageId, ...(before ? { beforeCode: before } : {}) });
    set({ jobs: takeColumn(get().jobs, stageId, answer.stage.map(hydrate)) });
  } catch (err) {
    await get().loadJobs();
    get().flash(
      err instanceof ApiError
        ? err.message
        : `Could not move ${code}. The board is back to what the shop has.`,
    );
  }
}

/** Change the board's stages the way a card move changes the board: on screen
 *  now, on the server next.
 *
 *  For the two stage writes that are actions rather than forms — a reorder and
 *  a recolour. They read as done the moment they happen, like a dragged card,
 *  so the list takes the change first and the server's answer replaces it. A
 *  refusal reloads the list rather than unwinding it by hand: whatever the
 *  server says the board is, is what it is. */
async function writeBoard(
  get: () => App,
  set: (partial: Partial<App>) => void,
  stages: Stage[],
  said: string,
  request: () => Promise<Stage[]>,
): Promise<void> {
  set({ stages });
  get().flash(said);
  try {
    set({ stages: await request() });
  } catch (err) {
    await get().loadStages();
    get().flash(
      err instanceof ApiError
        ? err.message
        : 'Could not change the board. Check the wifi — it is back to what the shop has.',
    );
  }
}

const storedTheme = (): 'light' | 'dark' => {
  try {
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

/* — how the jobs list is being read, kept between visits —
 *
 * The filter, the ordering, the page and its size and the search box are one
 * thing: the question the office is asking of the archive. The store already
 * holds it across a navigation — one store, and the screen reads it rather
 * than keeping its own — so this is the other half: it survives a reload too.
 *
 * It is worth surviving because narrowing this list is work. Somebody sets it
 * to Late, sorts by deadline and takes 100 rows to chase what is behind; a
 * refresh, an installed PWA resumed on Monday or a tab restored by the browser
 * should not put them back on "all 340, newest promise first" — a filter that
 * forgets is a filter the shop stops setting.
 *
 * It is this browser's, not the shop's, which is why it is here and not on the
 * server — and why signing out takes it with everything else. A search half
 * typed is the last person's, and so is the filter they left it on. */
type JobsView = Pick<AppState, 'jobFilter' | 'jobSort' | 'jobDir' | 'listPage' | 'listSize' | 'q'>;

/** What the list asks when nobody has asked anything else — and what signing
 *  out puts it back to. */
const DEFAULT_VIEW: JobsView = {
  jobFilter: 'all',
  // The list opens on the newest promise, which is the order it had before it
  // was sortable — and the one the caption has always claimed.
  jobSort: 'promised',
  jobDir: 'desc',
  listPage: 1,
  listSize: JOBS_PAGE_SIZE,
  q: '',
};

/** Read it back the way anything off a wire is read: one field at a time, and
 *  anything that is not a value this build offers is the default.
 *
 *  Storage is older than the code reading it — a stage filter from a previous
 *  build, a page size that has since gone — and these go straight into a
 *  request and into a pressed segment. An unchecked value would put the office
 *  in front of a filter row with nothing lit, asking the server for something
 *  it has to coerce. The page is the exception that needs no range check: the
 *  server clamps a page past the end and answers with the page it actually
 *  gave, which `loadJobList` takes back. */
const storedView = (): JobsView => {
  try {
    const raw = localStorage.getItem(VIEW_KEY);
    if (!raw) return DEFAULT_VIEW;
    const saved: unknown = JSON.parse(raw);
    if (!saved || typeof saved !== 'object') return DEFAULT_VIEW;
    const held = saved as Record<string, unknown>;
    const one = <T extends string>(key: keyof JobsView, allowed: readonly T[], fallback: T): T =>
      allowed.includes(held[key] as T) ? (held[key] as T) : fallback;
    const page = held.listPage;
    const size = held.listSize;
    const sizes: readonly number[] = PAGE_SIZES;

    return {
      jobFilter: one('jobFilter', JOB_FILTERS, DEFAULT_VIEW.jobFilter),
      jobSort: one('jobSort', JOB_SORTS, DEFAULT_VIEW.jobSort),
      jobDir: one('jobDir', SORT_DIRECTIONS, DEFAULT_VIEW.jobDir),
      listPage: typeof page === 'number' && Number.isInteger(page) && page >= 1 ? page : 1,
      listSize: typeof size === 'number' && sizes.includes(size) ? size : DEFAULT_VIEW.listSize,
      q: typeof held.q === 'string' ? held.q : '',
    };
  } catch {
    // Private browsing, storage disabled, or something else wrote the key.
    return DEFAULT_VIEW;
  }
};

/** Keep what is being asked. Called from `loadJobList`, which is the one thing
 *  every way of changing the question goes through — so there is no route to a
 *  new list that forgets to remember it. */
const rememberView = (st: AppState) => {
  const view: JobsView = {
    jobFilter: st.jobFilter,
    jobSort: st.jobSort,
    jobDir: st.jobDir,
    listPage: st.listPage,
    listSize: st.listSize,
    q: st.q,
  };
  try {
    localStorage.setItem(VIEW_KEY, JSON.stringify(view));
  } catch {
    // The list still works for this session; it just won't survive a reload.
  }
};

const forgetView = () => {
  try {
    localStorage.removeItem(VIEW_KEY);
  } catch {
    // Nothing to do — the store is cleared either way.
  }
};

/** Read once, on the way in: the store opens on whatever this browser was
 *  last looking at. */
const openingView = storedView();

export const useApp = create<App>()((set, get) => ({
  screen: 'board',
  origin: 'board',
  jobCode: '',
  theme: storedTheme(),

  authStatus: 'checking',
  session: null,
  authError: '',
  authNotice: '',
  signingIn: false,
  role: 'staff',

  jobs: [],
  jobsStatus: 'idle',
  jobsError: '',

  list: [],
  listStatus: 'idle',
  listError: '',
  // The page, its size and the ordering open on whatever this browser was last
  // asking — `DEFAULT_VIEW` when it has asked nothing yet.
  listPage: openingView.listPage,
  listPages: 1,
  listSize: openingView.listSize,
  listTotal: 0,
  listVisible: 0,
  jobSort: openingView.jobSort,
  jobDir: openingView.jobDir,

  // Nothing until the session lands — the board is the shop's, not this
  // build's, so there is no list here to fall back on.
  stages: [],
  stageOpen: false,
  stageEditId: null,
  stageDraft: { ...EMPTY_STAGE },
  stageError: '',
  savingStage: false,
  // Nothing until the session lands, for the same reason as the stages.
  colours: [],
  colourOpen: false,
  colourEditId: null,
  colourDraft: { ...EMPTY_COLOUR },
  colourError: '',
  savingColour: false,
  org: { ...EMPTY_ORG },
  savedOrg: { ...EMPTY_ORG },
  orgSaving: false,
  orgError: '',

  custs: [],
  custStatus: 'idle',
  custsError: '',
  custName: '',
  custList: [],
  custListStatus: 'idle',
  custListError: '',
  custPage: 1,
  custPages: 1,
  custSize: CUSTOMERS_PAGE_SIZE,
  custTotal: 0,
  custVisible: 0,
  custSort: 'name',
  custDir: 'asc',
  custQ: '',
  custOpen: false,
  custEditId: null,
  custDraft: { ...EMPTY_CUSTOMER },
  custError: '',
  savingCust: false,

  dash: null,
  dashStatus: 'idle',
  dashError: '',

  notices: [],
  unread: 0,
  noticesStatus: 'idle',
  noticesError: '',
  noticeOpen: false,
  noticeEditId: null,
  noticeDraft: { ...EMPTY_NOTICE },
  noticeError: '',
  savingNotice: false,

  people: [],
  peopleStatus: 'idle',
  peopleError: '',
  personOpen: false,
  personEditId: null,
  personDraft: { ...EMPTY_PERSON },
  personError: '',
  savingPerson: false,

  resetId: null,
  resetDraft: '',
  resetError: '',
  savingReset: false,

  pwOpen: false,
  pwCurrent: '',
  pwNext: '',
  pwError: '',
  savingPw: false,

  // Nothing is open until a job is. `openJob` fills all of this from the job
  // it opens, and until then there is nothing on screen for it to belong to.
  rows: [],
  savedRows: [],
  sel: [],
  justAdded: null,
  tasks: [],
  note: '',
  savedNote: '',
  jobTab: 'items',
  // A job opens showing everything about itself. Collapsing the header is a
  // step in, the way full board is on the board.
  jobHeadOpen: true,

  // The board opens on fit: the first question it answers is "what's in the
  // shop", and that wants every stage on the screen. Full board is a step in,
  // not the way in.
  boardMode: 'fit',
  // Every column short, until someone asks one for the detail.
  boardOpen: null,
  range: 7,
  // Opens on the Monday of the week the shop is in.
  rangeFrom: mondayOf(today()),
  railOpen: false,
  railSort: 'in',
  q: openingView.q,
  jobFilter: openingView.jobFilter,
  settings: 'colours',

  menuOpen: false,
  moveOpen: false,
  newOpen: false,
  jobEdit: null,
  jobEditFocus: null,
  picker: null,
  dropAt: null,
  toast: '',
  dialogBase: '',
  leaving: null,

  // Placeholders only: `openNewJob` fills the whole draft from the customer it
  // opens on, and it will not open at all until the shop has one.
  draft: {
    customer: '',
    title: '',
    ref: '',
    site: '',
    received: today(),
    colour: '',
    sheen: 1,
    profile: 'Square',
    items: 12,
    estStart: nextWorkday(today()),
    estDays: 5,
    deadline: addDays(nextWorkday(today()), 7),
  },

  set: (key, value) => set({ [key]: value } as Pick<AppState, typeof key>),

  flash: (msg) => {
    clearTimeout(toastTimer);
    set({ toast: msg });
    toastTimer = setTimeout(() => set({ toast: '' }), TOAST_MS);
  },

  loadSession: async () => {
    try {
      const session = await authApi.session();
      set({
        session,
        role: session.membership.role,
        authError: '',
        authNotice: '',
        // The shop's own details come in with the session — Settings →
        // Organisation reads the form off this, not off a fixture.
        org: orgFrom(session.organisation),
        savedOrg: orgFrom(session.organisation),
        orgError: '',
      });
      // Bind the shop's timezone before anything draws a date. Every "today",
      // every overdue check and every instant on screen is read in it, so it
      // has to be set before the first render rather than by a screen.
      setShopTimezone(session.organisation.timezone);
      // Customers are shop-wide data the board, the job form and the customer
      // screens all read, so they come with the session rather than waiting for
      // a screen to ask.
      //
      // The jobs are *awaited*, and the holding screen stays up until they
      // land: every landing screen is jobs, and the address bar is read back
      // the moment this says signed-in — so a refresh on /jobs/J-1042 has to
      // find J-1042 already there, or it would correct itself to the board.
      // The stages come with them for the same reason: they are the board's
      // columns, and a job sheet's progress track is drawn from them. So do
      // the colours — every card's chip is one, and a board of blank chips is
      // a board that reads as broken.
      const board = Promise.all([get().loadJobs(), get().loadStages(), get().loadColours()]);
      void get().loadCustomers();
      // The notice board comes with them, and for the same reason: the unread
      // count rides on the Dashboard tab from every screen, so it cannot wait
      // for somebody to visit the screen it is pointing at.
      void get().loadNotices();
      await board;
      set({ authStatus: 'signed-in' });
    } catch (err) {
      // A 401 is the ordinary "not signed in yet" answer, so it is not an error
      // to report. Anything else — the server is down, the wifi is out — is,
      // and saying so beats an unexplained sign-in screen.
      set({
        authStatus: 'signed-out',
        session: null,
        authError: err instanceof ApiError && !err.isSignedOut ? err.message : '',
      });
    }
  },

  signIn: async (email, password) => {
    if (get().signingIn) return;
    set({ signingIn: true, authError: '' });
    try {
      const session = await authApi.login({ email, password });
      set({
        session,
        role: session.membership.role,
        authError: '',
        authNotice: '',
        // The shop's own details come in with the session — Settings →
        // Organisation reads the form off this, not off a fixture.
        org: orgFrom(session.organisation),
        savedOrg: orgFrom(session.organisation),
        orgError: '',
      });
      // Bind the shop's timezone before anything draws a date. Every "today",
      // every overdue check and every instant on screen is read in it, so it
      // has to be set before the first render rather than by a screen.
      setShopTimezone(session.organisation.timezone);
      const board = Promise.all([get().loadJobs(), get().loadStages(), get().loadColours()]);
      void get().loadCustomers();
      // The notice board comes with them, and for the same reason: the unread
      // count rides on the Dashboard tab from every screen, so it cannot wait
      // for somebody to visit the screen it is pointing at.
      void get().loadNotices();
      await board;
      set({ authStatus: 'signed-in', signingIn: false, screen: 'board' });
    } catch (err) {
      set({
        signingIn: false,
        authError:
          err instanceof ApiError ? err.message : 'Could not sign in. Try again in a moment.',
      });
    }
  },

  signOut: async () => {
    // Signing out is a navigation like any other, and the one that takes the
    // most with it — the whole store goes. Ask first.
    if (unsaved(get())) {
      return get().leave(() => {
        void get().signOut();
      });
    }

    // Clear the session first: signing out must work even if the request fails,
    // or someone on bad wifi cannot get off a shared iPad.
    //
    // The toast goes too. It is the one piece of the shop that outlives a
    // screen change, and "Moved J-1042 to Topcoat" has no business sitting on
    // the sign-in screen of a shared iPad.
    clearTimeout(toastTimer);
    // A search half typed is the last person's, and so is the answer on its
    // way back to it.
    clearTimeout(searchTimer);
    clearTimeout(custSearchTimer);
    listRequest += 1;
    custRequest += 1;
    // And so is the filter they left the archive on. The stored copy goes with
    // the in-memory one, or the next person's reload would bring it back.
    forgetView();
    set({
      authStatus: 'signed-out',
      session: null,
      menuOpen: false,
      authError: '',
      toast: '',
      leaving: null,
      // The board is the shop's work, not this browser's. It goes with
      // everything else — nothing of the shop may sit on a sign-in screen.
      jobs: [],
      jobsStatus: 'idle',
      jobsError: '',
      // And its columns: the next shop to sign in on this iPad has its own.
      stages: [],
      stageOpen: false,
      stageEditId: null,
      // And its colours.
      colours: [],
      colourOpen: false,
      colourEditId: null,
      list: [],
      listStatus: 'idle',
      listError: '',
      ...DEFAULT_VIEW,
      jobCode: '',
      rows: [],
      savedRows: [],
      tasks: [],
      note: '',
      savedNote: '',
      // The dashboard is somebody's own work and the shop's numbers. Both go.
      dash: null,
      dashStatus: 'idle',
      dashError: '',
      // So does the notice board — and the count with it, or the next person to
      // sign in on a shared iPad would be told they have three unread notices
      // belonging to the last one.
      notices: [],
      unread: 0,
      noticesStatus: 'idle',
      noticesError: '',
      noticeOpen: false,
      noticeEditId: null,
      // The shop's own details are the shop's. So is its staff list.
      org: { ...EMPTY_ORG },
      savedOrg: { ...EMPTY_ORG },
      orgSaving: false,
      orgError: '',
      people: [],
      peopleStatus: 'idle',
      personOpen: false,
      personEditId: null,
      resetId: null,
      pwOpen: false,
      authNotice: '',
      // So does the customer list — names, contacts and sites are the shop's,
      // not this browser's.
      custs: [],
      custStatus: 'idle',
      custsError: '',
      custName: '',
      custList: [],
      custListStatus: 'idle',
      custListError: '',
      custPage: 1,
      custPages: 1,
      custSize: CUSTOMERS_PAGE_SIZE,
      custTotal: 0,
      custVisible: 0,
      custSort: 'name',
      custDir: 'asc',
      custQ: '',
      custOpen: false,
      custEditId: null,
    });
    try {
      await authApi.logout();
    } catch {
      // The cookies are gone from this browser either way; the server-side row
      // expires on its own.
    }
  },

  setTheme: (theme) => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Private browsing, or storage disabled. The theme still applies for
      // this session; it just won't survive a reload.
    }
    set({ theme });
  },

  /* The guard. Every action below that takes somebody off a screen asks it
     first, on itself: if there is nothing to lose the move happens now, and if
     there is, the move is parked and the shop is asked. Parking the action
     rather than the destination is what makes the recursion safe — `discard`
     puts the forms back before it re-runs the move, so the second pass finds
     nothing unsaved and goes. */
  leave: (run) => {
    const what = unsaved(get());
    if (!what) return run();
    set({ leaving: { what, run } });
  },

  stay: () => set({ leaving: null }),

  discard: () => {
    const st = get();
    // Discard means discard. The three page forms go back to what their last
    // save left, so the sentence the shop just read was true, and nothing
    // dirty follows them onto the next screen. A dialog needs no undoing —
    // it is closing, and the next open builds its form again.
    set({
      leaving: null,
      rows: st.savedRows,
      sel: [],
      justAdded: null,
      note: st.savedNote,
      org: st.savedOrg,
      orgError: '',
    });
    st.leaving?.run();
  },

  go: (screen) => {
    // Going where you already are is not leaving — the settings sections do
    // it, and asking there would be a warning about nothing.
    if (screen !== get().screen && unsaved(get())) return get().leave(() => get().go(screen));
    set({ screen, moveOpen: false, picker: null });
  },

  openJob: (code) => {
    const st = get();
    const job = st.jobs.find((j) => j.code === code);
    if (!job) return;
    // Opening a job reads its item lines and its note back from scratch, so
    // anything typed into the last one is gone whether it is the same job or
    // a different one.
    if (unsaved(st)) return st.leave(() => get().openJob(code));
    // Coming from a detail screen keeps the original origin, so Back from an
    // item list still lands on the board rather than the job you came through.
    const origin = st.screen === 'job' || st.screen === 'items' ? st.origin : st.screen;
    set({
      screen: 'job',
      jobCode: code,
      origin,
      moveOpen: false,
      sel: [],
      justAdded: null,
      rows: itemsFor(job),
      savedRows: itemsFor(job),
      tasks: tasksFor(job, st.stages),
      note: job.note,
      savedNote: job.note,
      // Every job opens on its items. Which tab the last job was left on says
      // nothing about this one.
      jobTab: 'items',
    });
  },

  openCustomer: (name) => {
    if (unsaved(get())) return get().leave(() => get().openCustomer(name));
    set({ screen: 'customer', custName: name, menuOpen: false });
  },

  goBack: () => {
    const st = get();
    if (unsaved(st)) return st.leave(() => get().goBack());
    const { origin } = st;
    set({ screen: origin === 'customer' ? 'customer' : origin, moveOpen: false, picker: null });
  },

  loadJobs: async () => {
    set({ jobsStatus: 'loading', jobsError: '' });
    try {
      const page = await jobsApi.list({ pageSize: BOARD_PAGE_SIZE, sort: 'board', dir: 'asc' });
      set({ jobs: page.rows.map(hydrate), jobsStatus: 'ready', jobsError: '' });
    } catch (err) {
      set({ jobsStatus: 'error', jobsError: err instanceof ApiError ? err.message : OFFLINE_JOBS });
    }
  },

  loadJobList: async () => {
    const mine = (listRequest += 1);
    // The question, kept before it is asked — every heading, filter, search and
    // pager click ends up here, so this is the one place it has to be kept.
    rememberView(get());
    set({ listStatus: 'loading', listError: '' });
    try {
      const page = await jobsApi.list(listQuery(get()));
      // A slower answer to an older question is not this list.
      if (mine !== listRequest) return;
      set({
        list: page.rows.map(hydrate),
        // The server clamps a page past the end rather than answering nothing,
        // so take the page it actually gave back — otherwise the pager would
        // keep claiming a page the office is not on.
        listPage: page.page,
        listPages: page.pageCount,
        // The server clamps a page size it will not serve, so this is the
        // size a page actually held — never the one that was asked for.
        listSize: page.pageSize,
        listTotal: page.total,
        listVisible: page.totalVisible,
        listStatus: 'ready',
        listError: '',
      });
    } catch (err) {
      if (mine !== listRequest) return;
      set({ listStatus: 'error', listError: err instanceof ApiError ? err.message : OFFLINE_JOBS });
    }
  },

  sortJobsBy: (column) => {
    const st = get();
    // Turning the column you are already sorted by is what a second click
    // means. A new column starts where that column is most useful: a job code
    // counts up, and a date list opens on the newest — which is the order this
    // screen had before any of it was clickable.
    const dir: SortDirection =
      st.jobSort === column
        ? st.jobDir === 'asc'
          ? 'desc'
          : 'asc'
        : column === 'code'
          ? 'asc'
          : 'desc';
    // Back to page 1: page 4 of one ordering is nowhere in particular in
    // another, and landing there would read as jobs having gone missing.
    set({ jobSort: column, jobDir: dir, listPage: 1 });
    void get().loadJobList();
  },

  goToJobPage: (page) => {
    set({ listPage: Math.max(1, page) });
    void get().loadJobList();
  },

  setJobPageSize: (size) => {
    set({ listSize: size, listPage: 1 });
    void get().loadJobList();
  },

  setJobQuery: (q) => {
    set({ q, listPage: 1 });
    // Every letter typed is otherwise a request, and the shop’s wifi is the
    // reason this waits. The box itself is not held back — what is on screen is
    // what was typed, the moment it was typed; only the asking waits.
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => void get().loadJobList(), SEARCH_MS);
  },

  setJobFilter: (filter) => {
    set({ jobFilter: filter, listPage: 1 });
    void get().loadJobList();
  },

  move: (stageId) => {
    const st = get();
    set({ moveOpen: false });
    void st.tryMove(st.jobCode, stageId);
  },

  /** Move a card to the end of a stage — a card dropped on the column
   *  background, and the move sheet on the job sheet. */
  tryMove: async (code, stageId) => {
    const st = get();
    const src = st.jobs.find((j) => j.code === code);
    if (!src) return;
    if (!canEdit(st)) {
      set({ dropAt: null });
      return st.flash(NOT_ON_IT);
    }
    const to = st.jobs.filter((j) => j.stage === stageId && j.code !== code).length;
    st.flash(
      src.stage === stageId
        ? `${code} moved to the end of ${stageName(st, stageId)}`
        : `Moved ${code} to ${stageName(st, stageId)}`,
    );
    await placeOnBoard(get, set, code, stageId, to);
  },

  beginDrag: (code) => {
    dragging = code;
  },

  dropOnColumn: (stageId) => {
    const code = dragging;
    dragging = null;
    if (!code) return set({ dropAt: null });
    get().tryMove(code, stageId);
  },

  endDrag: () => {
    dragging = null;
    set({ dropAt: null });
  },

  hoverCard: (code, before) => {
    if (!dragging || dragging === code) return;
    const at = get().dropAt;
    if (!at || at.code !== code || at.before !== before) set({ dropAt: { code, before } });
  },

  dropOnCard: async (targetCode) => {
    const st = get();
    const code = dragging;
    dragging = null;
    if (!code || code === targetCode) return set({ dropAt: null });
    const src = st.jobs.find((j) => j.code === code);
    const target = st.jobs.find((j) => j.code === targetCode);
    if (!src || !target) return set({ dropAt: null });
    if (!canEdit(st)) {
      set({ dropAt: null });
      return st.flash(NOT_ON_IT);
    }

    // Where it lands, counted within the target’s stage and with the dragged
    // card already taken out of it — otherwise dragging a card downwards inside
    // its own column would land it one place short of where it was dropped.
    const above = st.dropAt?.code === targetCode ? st.dropAt.before : true;
    const column = st.jobs.filter((j) => j.stage === target.stage && j.code !== code);
    const to = column.findIndex((j) => j.code === targetCode) + (above ? 0 : 1);

    st.flash(
      src.stage === target.stage
        ? `${code} reordered in ${stageName(st, target.stage)}`
        : `Moved ${code} to ${stageName(st, target.stage)}`,
    );
    await placeOnBoard(get, set, code, target.stage, to);
  },

  nudge: async (code, dir) => {
    const st = get();
    const job = st.jobs.find((x) => x.code === code);
    if (!job) return;
    if (!canEdit(st)) return st.flash(NOT_ON_IT);
    const column = st.jobs.filter((x) => x.stage === job.stage);
    const to = column.findIndex((x) => x.code === code) + dir;
    if (to < 0 || to >= column.length) return;

    st.flash(`${code} moved ${dir < 0 ? 'up' : 'down'} in ${stageName(st, job.stage)}`);
    await placeOnBoard(get, set, code, job.stage, to);
  },

  setRow: (id, patch) =>
    set({ rows: get().rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) }),

  clearJustAdded: () => set({ justAdded: null }),

  addRow: () => {
    const { rows } = get();
    const last = rows[rows.length - 1];
    if (!last) return;
    const id = nextRowId(rows);
    set({ rows: renumber(rows.concat([{ ...last, id, qty: 1 }])), justAdded: id });
  },

  duplicateRow: (id) => {
    const { rows } = get();
    const i = rows.findIndex((r) => r.id === id);
    if (i < 0) return;
    const made = nextRowId(rows);
    set({
      rows: renumber(rows.slice(0, i + 1).concat([{ ...rows[i], id: made }], rows.slice(i + 1))),
      justAdded: made,
    });
  },

  duplicateLast: () => {
    const st = get();
    const last = st.rows[st.rows.length - 1];
    if (!last) return;
    const id = nextRowId(st.rows);
    set({ rows: renumber(st.rows.concat([{ ...last, id }])), justAdded: id });
    st.flash(`Item ${st.rows.length + 1} — ${last.qty} × ${last.type}. Change what differs.`);
  },

  deleteRow: (id) =>
    set({
      rows: renumber(get().rows.filter((r) => r.id !== id)),
      sel: get().sel.filter((x) => x !== id),
      justAdded: get().justAdded === id ? null : get().justAdded,
    }),

  toggleSelected: (id) => {
    const { sel } = get();
    set({ sel: sel.includes(id) ? sel.filter((x) => x !== id) : sel.concat(id) });
  },

  clearSelection: () => set({ sel: [] }),

  deleteSelected: () => {
    const st = get();
    const n = st.sel.length;
    set({
      rows: renumber(st.rows.filter((r) => !st.sel.includes(r.id))),
      sel: [],
      justAdded: null,
    });
    st.flash(`${n} rows deleted.`);
  },

  saveItems: async () => {
    const st = get();
    const job = currentJob(st);
    if (!job) return;
    if (!canEdit(st)) {
      return st.flash('Read-only — you’re not on this job, so the item lines can’t be saved.');
    }
    const totalQty = st.rows.reduce((a, r) => a + r.qty, 0);
    const totalArea = st.rows.reduce(
      (a, r) => a + ((r.w * r.h) / 1e6) * r.qty * (r.faces === 2 ? 2 : 1),
      0,
    );
    invalidateArea(job.code);
    // The lines themselves are still the client's — `itemsFor` is the seam, and
    // they follow the jobs into the database next. What the job carries is the
    // count, and that is a column, so it goes now.
    set({ screen: 'job', savedRows: st.rows });
    st.flash(
      `${st.rows.length} item lines saved to ${job.code} — ${totalQty} components, ${totalArea.toFixed(1)} m².`,
    );
    await writeJob(get, set, job.code, { items: totalQty });
  },

  toggleTask: (index) => {
    const st = get();
    if (!canEdit(st)) {
      return st.flash("Read-only — you're not on this job, so tasks can't be ticked.");
    }
    set({
      tasks: st.tasks.map((t, k) =>
        k === index ? { ...t, done: !t.done, due: !t.done ? 'Done' : 'Tue 1 Sep' } : t,
      ),
    });
  },

  setNote: (note) => set({ note }),

  /* The two Saves that stand over a form on a page rather than in a dialog.
     Each one sends its change and moves its baseline on the answer, which is
     what makes "unsaved" a true statement either way. */
  saveNote: async () => {
    const st = get();
    const job = currentJob(st);
    if (!job) return;
    if (!canEdit(st)) {
      return st.flash('Read-only — you’re not on this job, so the note can’t be saved.');
    }
    const note = st.note;
    set({ savedNote: note });
    st.flash(note.trim() ? 'Note saved.' : 'Note cleared.');
    await writeJob(get, set, job.code, { note });
  },

  setOrg: (patch) => set({ org: { ...get().org, ...patch }, orgError: '' }),

  /* The shop's own details. Unlike the note, this one waits on the server
     before it says anything: the name is on every screen and the job code
     prefix decides what the next job is called, so a form that said "saved"
     and then quietly lost the ABN would be worse than one that took a moment.
     A refusal stays under the form — what the office typed is still there to
     fix. */
  saveOrg: async () => {
    const st = get();
    if (!isOffice(st)) return st.flash('Only the owner and the office change the shop’s details.');
    if (st.orgSaving) return;

    const org = st.org;
    // Send only what changed, which is also what stops a form nobody edited
    // being refused for a field it never touched.
    const patch: UpdateOrganisationRequest = {};
    if (org.name !== st.savedOrg.name) patch.name = org.name;
    if (org.abn !== st.savedOrg.abn) patch.abn = org.abn;
    if (org.timezone !== st.savedOrg.timezone) patch.timezone = org.timezone;
    if (org.jobCodePrefix !== st.savedOrg.jobCodePrefix) patch.jobCodePrefix = org.jobCodePrefix;
    if (Object.keys(patch).length === 0) return st.flash('Nothing was changed.');

    set({ orgSaving: true, orgError: '' });
    try {
      const saved = await organisationApi.update(patch);
      // The timezone may have just changed, and every date on the next screen
      // is read in it — so it is rebound here rather than at the next sign-in.
      setShopTimezone(saved.timezone);
      const session = get().session;
      set({
        org: orgFrom(saved),
        savedOrg: orgFrom(saved),
        orgSaving: false,
        orgError: '',
        // The session carries the shop too — the account menu and the next job
        // code read it — so it moves with the form rather than going stale
        // until the next sign-in.
        session: session ? { ...session, organisation: saved } : session,
      });
      get().flash('Organisation settings saved.');
    } catch (err) {
      set({ orgSaving: false, orgError: refusalOf(err) });
    }
  },

  openPicker: (kind, rowId, stageId) => set({ picker: { kind, rowId, stageId } }),
  closePicker: () => set({ picker: null }),

  applyToRows: (patch) => {
    const st = get();
    const target = st.picker?.rowId ?? null;
    if (target === null) {
      const sel = st.sel;
      set({
        rows: st.rows.map((r) => (sel.includes(r.id) ? { ...r, ...patch } : r)),
        picker: null,
        sel: [],
      });
      st.flash(`Set on ${sel.length} rows`);
    } else {
      set({
        rows: st.rows.map((r) => (r.id === target ? { ...r, ...patch } : r)),
        picker: null,
      });
    }
  },

  setStageTone: (stageId, hex) => {
    const st = get();
    set({ picker: null });
    if (!isOffice(st)) return st.flash(OFFICE_ONLY);
    void writeBoard(
      get,
      set,
      st.stages.map((x) => (x.id === stageId ? { ...x, tone: hex } : x)),
      `${stageName(st, stageId)} set to ${(TONE_NAME[hex] ?? 'custom').toLowerCase()} on the board.`,
      () => stagesApi.update(stageId, { tone: hex }),
    );
  },

  loadStages: async () => {
    try {
      set({ stages: await stagesApi.list() });
    } catch (err) {
      // The list on screen stays as it was — an empty board on the way in,
      // which says as much — and the shop is told why.
      get().flash(
        err instanceof ApiError
          ? err.message
          : 'Could not load the board’s stages. Check the wifi and try again.',
      );
    }
  },

  openAddStage: () =>
    set({
      stageOpen: true,
      stageEditId: null,
      stageDraft: { ...EMPTY_STAGE },
      stageError: '',
      dialogBase: JSON.stringify(EMPTY_STAGE),
    }),

  openEditStage: (stage) => {
    const stageDraft: StageDraft = {
      name: stage.name,
      tone: stage.tone,
      // 0 is no limit and the field says so in words, so it opens empty
      // rather than showing a zero the office has to clear first.
      wip: stage.wip > 0 ? String(stage.wip) : '',
      def: Boolean(stage.def),
      terminal: Boolean(stage.terminal),
    };
    set({
      stageOpen: true,
      stageEditId: stage.id,
      stageDraft,
      stageError: '',
      dialogBase: JSON.stringify(stageDraft),
    });
  },

  setStageDraft: (patch) => set({ stageDraft: { ...get().stageDraft, ...patch }, stageError: '' }),

  closeStage: () => set({ stageOpen: false, stageEditId: null, stageError: '' }),

  /** Add a stage, or save a change to one. Everything refused here is refused
   *  because the board stops making sense otherwise, and the message says
   *  which — a refusal the office can act on, not a warning to click past.
   *
   *  Like the shop's own details, this waits on the server before it says
   *  anything: the whole shop reads this list, and a form that said "saved"
   *  and then quietly lost the rename on the next reload is how the office
   *  learns not to trust it. A refusal stays in the form with what was typed. */
  saveStage: async () => {
    const st = get();
    if (st.savingStage) return;
    if (!isOffice(st)) return set({ stageError: OFFICE_ONLY });

    const d = st.stageDraft;
    const id = st.stageEditId;
    const existing = id === null ? undefined : st.stages.find((x) => x.id === id);
    if (id !== null && !existing) {
      return set({ stageError: 'That stage is no longer on the board.' });
    }

    const name = d.name.trim().replace(/\s+/g, ' ');
    if (!name) {
      return set({ stageError: 'A stage needs a name — the board reads it off this list.' });
    }

    const clash = st.stages.find((x) => x.id !== id && x.name.toLowerCase() === name.toLowerCase());
    if (clash) {
      return set({
        stageError: `There is already a stage called ${clash.name}. Two columns with one name is a board nobody can read.`,
      });
    }

    const wip = d.wip.trim() === '' ? 0 : Number(d.wip);
    if (!Number.isInteger(wip) || wip < 0) {
      return set({ stageError: 'A WIP limit is a whole number of jobs, or blank for no limit.' });
    }

    if (d.def && d.terminal) {
      return set({
        stageError:
          'A new job cannot start at a stage that means the work has already left the shop. Pick one.',
      });
    }
    // A new job has to land somewhere, so the default can move but never go.
    if (existing?.def && !d.def) {
      return set({
        stageError: `New jobs land at ${existing.name}. Make another stage the default first — they have to land somewhere.`,
      });
    }
    // Jobs sitting at a stage that has just been marked terminal would count as
    // out of the shop with nobody having moved them.
    if (existing && d.terminal && !existing.terminal) {
      const held = st.jobs.filter((j) => j.stage === existing.id).length;
      if (held) {
        const it = held === 1 ? 'it' : 'them';
        return set({
          stageError: `${held} ${held === 1 ? 'job is' : 'jobs are'} in ${existing.name}. Marking it terminal takes ${it} out of the shop without anyone moving ${it}.`,
        });
      }
    }

    // The id is the server's to make, from the name, and a rename never changes
    // it. Taking the default off whichever stage carried it is the server's too
    // — the answer is the whole board, both stages included.
    const body = { name, tone: d.tone, wip, def: d.def, terminal: d.terminal };

    set({ savingStage: true, stageError: '' });
    try {
      const stages = existing
        ? await stagesApi.update(existing.id, body)
        : await stagesApi.create(body);
      set({ stages, savingStage: false, stageOpen: false, stageEditId: null, stageError: '' });
      get().flash(
        existing
          ? `${name} saved — the board is reading it now.`
          : `${name} added to the end of the board.`,
      );
    } catch (err) {
      set({ savingStage: false, stageError: refusalOf(err) });
    }
  },

  /** Archive a stage: it comes off the board. Only ever an empty one — a stage
   *  cannot take its jobs with it. The count here is the jobs this browser has
   *  on its board; the server counts every job in the shop, and says so if one
   *  the office cannot see is still standing there. */
  archiveStage: async () => {
    const st = get();
    if (st.savingStage) return;
    if (!isOffice(st)) return set({ stageError: OFFICE_ONLY });
    const stage = st.stages.find((x) => x.id === st.stageEditId);
    if (!stage) return;

    const held = st.jobs.filter((j) => j.stage === stage.id).length;
    if (held) {
      const it = held === 1 ? 'it' : 'them';
      return set({
        stageError: `${held} ${held === 1 ? 'job is' : 'jobs are'} in ${stage.name}. Move ${it} first — a stage cannot take its jobs with it.`,
      });
    }
    if (stage.def) {
      return set({
        stageError: `New jobs land at ${stage.name}. Make another stage the default first.`,
      });
    }
    if (st.stages.length === 1) {
      return set({ stageError: 'The board needs at least one stage.' });
    }

    set({ savingStage: true, stageError: '' });
    try {
      const stages = await stagesApi.archive(stage.id);
      set({ stages, savingStage: false, stageOpen: false, stageEditId: null, stageError: '' });
      get().flash(`${stage.name} archived — it is off the board.`);
    } catch (err) {
      set({ savingStage: false, stageError: refusalOf(err) });
    }
  },

  /** Reorder by one place. This is the keyboard path and what the drag lands
   *  on; drag is a pointer enhancement, never the only way to reorder. */
  moveStage: (id, dir) => {
    const st = get();
    if (!isOffice(st)) return st.flash(OFFICE_ONLY);
    const stages = st.stages.slice();
    const i = stages.findIndex((x) => x.id === id);
    const to = i + dir;
    if (i < 0 || to < 0 || to >= stages.length) return;
    [stages[i], stages[to]] = [stages[to], stages[i]];
    void writeBoard(get, set, stages, `${stages[to].name} is now stage ${to + 1}.`, () =>
      stagesApi.reorder({ ids: stages.map((x) => x.id) }),
    );
  },

  beginStageDrag: (id) => {
    draggingStage = id;
  },

  dropOnStage: (targetId) => {
    const st = get();
    const id = draggingStage;
    draggingStage = null;
    if (!id || id === targetId) return;
    if (!isOffice(st)) return st.flash(OFFICE_ONLY);
    const stages = st.stages.slice();
    const from = stages.findIndex((x) => x.id === id);
    const to = stages.findIndex((x) => x.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = stages.splice(from, 1);
    stages.splice(to, 0, moved);
    void writeBoard(get, set, stages, `${moved.name} is now stage ${to + 1}.`, () =>
      stagesApi.reorder({ ids: stages.map((x) => x.id) }),
    );
  },

  endStageDrag: () => {
    draggingStage = null;
  },

  loadColours: async () => {
    try {
      set({ colours: await coloursApi.list() });
    } catch (err) {
      // The list on screen stays as it was, and the shop is told why.
      get().flash(
        err instanceof ApiError
          ? err.message
          : 'Could not load the colour list. Check the wifi and try again.',
      );
    }
  },

  openAddColour: () =>
    set({
      colourOpen: true,
      colourEditId: null,
      colourDraft: { ...EMPTY_COLOUR },
      colourError: '',
      dialogBase: JSON.stringify(EMPTY_COLOUR),
    }),

  openEditColour: (colour) => {
    const colourDraft: ColourDraft = {
      brand: colour.brand,
      code: colour.code,
      name: colour.name,
      hex: colour.hex,
    };
    set({
      colourOpen: true,
      colourEditId: colour.id,
      colourDraft,
      colourError: '',
      dialogBase: JSON.stringify(colourDraft),
    });
  },

  setColourDraft: (patch) =>
    set({ colourDraft: { ...get().colourDraft, ...patch }, colourError: '' }),

  closeColour: () => set({ colourOpen: false, colourEditId: null, colourError: '' }),

  /** Add a colour, or save a change to one. Waits on the server before it
   *  says anything, like the stage form: every job card draws from this list,
   *  and a form that said "saved" and lost the code on the next reload is how
   *  the office learns not to trust it. A refusal stays in the form. */
  saveColour: async () => {
    const st = get();
    if (st.savingColour) return;
    if (!isOffice(st)) return set({ colourError: OFFICE_ONLY_COLOURS });

    const id = st.colourEditId;
    const existing = id === null ? undefined : st.colours.find((c) => c.id === id);
    if (id !== null && !existing) {
      return set({ colourError: 'That colour is not on the shop’s list any more.' });
    }

    const tidy = (value: string) => value.trim().replace(/\s+/g, ' ');
    const d = st.colourDraft;
    const brand = tidy(d.brand);
    const code = tidy(d.code);
    const name = tidy(d.name);
    const hex = d.hex.trim().toUpperCase();

    if (!brand) {
      return set({
        colourError: 'A colour needs its brand — Dulux, Taubmans — so it can be re-ordered.',
      });
    }
    if (!code) return set({ colourError: 'A colour needs its code. It is what gets re-ordered.' });
    if (!name) return set({ colourError: 'A colour needs its name — what the shop calls it.' });
    if (!/^#[0-9A-F]{6}$/.test(hex)) {
      return set({
        colourError: 'The swatch is a colour like #F7F6F1. Pick it from the swatch.',
      });
    }

    // One code, one colour. An archived one is not a clash when adding — the
    // server brings it back — but it is when editing another colour onto it.
    const clash = colourWithCode(st, code, id);
    if (clash && (!clash.archived || existing)) {
      return set({
        colourError: clash.archived
          ? `${clash.code} is ${clash.brand} ${clash.name}, which is archived. Bring that one back from the list instead.`
          : `${clash.code} is already on the list — ${clash.brand} ${clash.name}. One code is one colour.`,
      });
    }

    set({ savingColour: true, colourError: '' });
    try {
      const colours = existing
        ? await coloursApi.update(existing.id, { brand, code, name, hex })
        : await coloursApi.create({ brand, code, name, hex });
      // A corrected code carried its jobs with it on the server. The board is
      // holding the old spelling, so it is read again rather than patched.
      const recoded = existing && existing.code !== code;
      set({ colours, savingColour: false, colourOpen: false, colourEditId: null });
      if (recoded) void get().loadJobs();
      get().flash(
        existing
          ? recoded && existing.jobs > 0
            ? `${brand} ${name} saved — ${existing.jobs} ${existing.jobs === 1 ? 'job moves' : 'jobs move'} to ${code} with it.`
            : `${brand} ${name} saved.`
          : clash
            ? `${brand} ${name} (${code}) is back on the list.`
            : `${brand} ${name} (${code}) added to the list.`,
      );
    } catch (err) {
      set({ savingColour: false, colourError: refusalOf(err) });
    }
  },

  /** Take a colour off the pickers. It is an action, not a form — nothing is
   *  typed, and Bring back undoes it — so it goes from the row with no dialog.
   *  The jobs in it keep it, and the toast says so, because "archived" on its
   *  own reads as though the colour has gone off forty job cards. */
  archiveColour: async (colour) => {
    const st = get();
    if (st.savingColour) return;
    if (!isOffice(st)) return st.flash(OFFICE_ONLY_COLOURS);
    set({ savingColour: true });
    try {
      set({ colours: await coloursApi.archive(colour.id), savingColour: false });
      get().flash(
        `${colour.brand} ${colour.name} archived — the pickers stop offering it, and jobs already in it keep it.`,
      );
    } catch (err) {
      set({ savingColour: false });
      get().flash(refusalOf(err));
    }
  },

  restoreColour: async (colour) => {
    const st = get();
    if (st.savingColour) return;
    if (!isOffice(st)) return st.flash(OFFICE_ONLY_COLOURS);
    set({ savingColour: true });
    try {
      set({ colours: await coloursApi.restore(colour.id), savingColour: false });
      get().flash(`${colour.brand} ${colour.name} is back on the list.`);
    } catch (err) {
      set({ savingColour: false });
      get().flash(refusalOf(err));
    }
  },

  setJobColour: async (code) => {
    const st = get();
    const job = currentJob(st);
    const colour = colourOf(st, code);
    if (!job || !colour) return;
    set({
      picker: null,
      jobs: st.jobs.map((j) => (j.code === job.code ? { ...j, colour: code } : j)),
      rows: st.rows.map((r) => ({ ...r, colour: code })),
      // The job's colour is changed there and then — the toast says every line
      // took it. So the same change goes onto what counts as saved: a job
      // recoloured with the item editor clean must not leave it looking dirty,
      // and one recoloured with edits pending must not swallow them.
      savedRows: st.savedRows.map((r) => ({ ...r, colour: code })),
    });
    st.flash(
      `${job.code} changed to ${colour.brand} ${colour.name} (${colour.code}) — all ${st.rows.length} item lines updated.`,
    );
    await writeJob(get, set, job.code, { colour: code });
  },

  openJobEdit: (focus) => {
    const st = get();
    const job = currentJob(st);
    if (!job) return;
    // The same answer the read-only header already gives, for anyone who
    // reaches the form another way.
    if (!canEdit(st)) {
      return st.flash('Read-only — you’re not on this job, so its details can’t be changed.');
    }
    const form: JobEdit = {
      title: job.title,
      // An em dash and "Site not set" are what the screen draws for a blank —
      // never what the record holds. The field opens empty rather than with a
      // dash in it to delete first.
      ref: job.ref === '—' ? '' : job.ref,
      site: job.site === 'Site not set' ? '' : job.site,
      received: job.received,
      promised: job.promised,
      estStart: job.plan.estStart,
      estDays: job.plan.estDays,
      deadline: job.plan.deadline,
    };
    set({ jobEdit: form, jobEditFocus: focus ?? null, dialogBase: JSON.stringify(form) });
  },

  setJobEdit: (patch) => {
    const form = get().jobEdit;
    if (!form) return;
    set({ jobEdit: { ...form, ...patch } });
  },

  saveJob: async () => {
    const st = get();
    const job = currentJob(st);
    const form = st.jobEdit;
    if (!job || !form) return;
    if (!canEdit(st)) {
      return st.flash('Read-only — you’re not on this job, so its details can’t be saved.');
    }

    const title = form.title.trim();
    if (!title) return st.flash('Give the job a title so the floor knows what it is.');

    // The one arithmetic rule on the form, said again here: the deadline is the
    // day it must be out by, and an estimate that finishes after it is not a
    // plan. Save is dead while that holds, so this is for every other way in.
    const plan: Plan = {
      estStart: form.estStart,
      estDays: form.estDays,
      deadline: form.deadline,
    };
    if (atRisk(plan)) {
      return st.flash(
        'The deadline is before the work is done. Move the deadline out, start earlier, or shorten the estimate.',
      );
    }

    const ref = form.ref.trim();
    const site = form.site.trim();
    const patch: UpdateJobRequest = {
      title,
      ref,
      site,
      receivedAt: form.received,
      promisedAt: form.promised,
      estStartAt: form.estStart,
      estDays: form.estDays,
      deadlineAt: form.deadline,
    };

    // On the screen now, in the database next — every other write on a job
    // goes the same way. The two derived fields are derived the way `hydrate`
    // derives them, so the answer coming back changes nothing.
    set({
      jobEdit: null,
      jobEditFocus: null,
      jobs: st.jobs.map((j) =>
        j.code === job.code
          ? {
              ...j,
              title,
              ref: ref || '—',
              site: site || 'Site not set',
              received: form.received,
              promised: form.promised,
              plan,
            }
          : j,
      ),
    });
    st.flash(
      `${job.code} saved — promised ${formatDay(form.promised)}, out by ${formatDay(form.deadline)}.`,
    );

    // A site typed on the job is a site the customer has, the same as one
    // typed on the way in. Nobody sees it here who cannot see the customer.
    const cust = st.custs.find((c) => c.id === job.customerId);
    if (site && cust && !cust.sites.includes(site)) void st.addSite(cust, site);

    await writeJob(get, set, job.code, patch);
  },

  openNewJob: (customer) => {
    const st = get();
    const on = customer ?? st.custs[0];
    // A job belongs to a customer, so there has to be one. Say that rather than
    // opening a form whose first field cannot be filled in.
    if (!on) {
      return st.flash('Add the customer first — every job belongs to one.');
    }
    // The list is most-used first, so the colour a new job opens on is the one
    // the shop sprays most. A job has to be in one, so there has to be one.
    const colour = liveColours(st)[0];
    if (!colour) {
      return st.flash('Add a colour first, in Settings → Colours — every job is sprayed in one.');
    }
    const draft: JobDraft = {
      customer: on.name,
      title: '',
      ref: on.ref ? `${on.ref}-` : '',
      site: on.sites[0] ?? '',
      // Today where the shop is — never the tablet's today, which is what
      // used to put a job in last week on a machine brought back from Perth.
      received: today(),
      colour: colour.code,
      sheen: 1,
      profile: 'Square',
      items: 12,
      // Est. days are working days, so the plan starts on one.
      estStart: nextWorkday(today()),
      estDays: 5,
      deadline: addDays(nextWorkday(today()), 7),
    };
    set({ newOpen: true, draft, dialogBase: JSON.stringify(draft) });
  },

  setDraft: (patch) => set({ draft: { ...get().draft, ...patch } }),

  createJob: async () => {
    const st = get();
    const d = st.draft;
    if (!d.title.trim()) return st.flash('Give the job a title so the floor knows what it is.');

    // Nothing to validate about the dates any more: the form holds `IsoDay`s
    // straight off a native date picker, which cannot hand back a day that is
    // not one. The check that used to be here parsed the shop's own labels back
    // into days, and it went with them.

    // A job belongs to a customer, and the customer is a record now — so the
    // form's name has to resolve to one before anything is written.
    const cust = st.custs.find((c) => c.name === d.customer);
    if (!cust) return st.flash(`${d.customer} is not on the customer list. Add them first.`);

    const stage = defaultStage(st);
    if (!stage) return st.flash('The board has no stages. Add one in Settings first.');

    const site = d.site.trim();

    let job: Job;
    try {
      job = hydrate(
        await jobsApi.create({
          customerId: cust.id,
          stageId: stage.id,
          title: d.title.trim(),
          ref: d.ref,
          colour: d.colour,
          sheen: d.sheen,
          profile: d.profile,
          items: Math.max(1, d.items),
          site,
          receivedAt: d.received,
          // The date the shop works to is the date the customer was told, on a
          // job being taken in. They part company later, when a plan slips.
          promisedAt: d.deadline,
          estStartAt: d.estStart,
          estDays: d.estDays,
          deadlineAt: d.deadline,
        }),
      );
    } catch (err) {
      // The form stays open with what they typed — a customer to pick or a
      // title to fix is fixable in place, and retyping the rest is not a
      // punishment anybody deserves.
      return st.flash(
        err instanceof ApiError ? err.message : 'Could not take the job in. Try again in a moment.',
      );
    }

    // A site typed on the job form is a new site for the customer, and the
    // customer is a real record too, so it is saved rather than remembered.
    if (site && !cust.sites.includes(site)) {
      void st.addSite(cust, site);
    }

    set({
      jobs: st.jobs.concat([job]),
      newOpen: false,
      screen: 'job',
      // Back lands where the job was started — the customer's page when it was
      // started there, the board otherwise.
      origin: st.screen === 'customer' ? 'customer' : 'board',
      jobCode: job.code,
      rows: itemsFor(job),
      savedRows: itemsFor(job),
      justAdded: null,
      tasks: tasksFor(job, st.stages),
      note: '',
      savedNote: '',
    });
    st.flash(`${job.code} created for ${d.customer} — it starts at ${stageName(st, job.stage)}.`);
  },

  loadCustomers: async () => {
    const st = get();
    if (st.custStatus === 'loading') return;
    // A subcontractor gets a 403 by design — they never see customer detail —
    // so don't ask, and don't put an error where the explanation belongs.
    if (st.session?.membership.role === 'subcontractor') return;

    set({ custStatus: 'loading', custsError: '' });
    try {
      const { rows: custs } = await customersApi.list({ pageSize: CUSTOMERS_ALL_SIZE });
      // Keep the open customer if they are still there, otherwise show the
      // first: the customer page must never land on a name nobody has.
      const custName = custs.some((c) => c.name === get().custName)
        ? get().custName
        : (custs[0]?.name ?? '');
      set({ custs, custName, custStatus: 'ready', custsError: '' });
    } catch (err) {
      set({
        custStatus: 'error',
        custsError:
          err instanceof ApiError
            ? err.message
            : 'Could not load the customers. Check the wifi and try again.',
      });
    }
  },

  loadCustomerList: async () => {
    const st = get();
    // A previewed subcontractor would be refused by the server — they never see
    // customer detail — so don't ask, and don't put an error where the
    // explanation belongs.
    if (st.session?.membership.role === 'subcontractor') return;

    const mine = (custRequest += 1);
    set({ custListStatus: 'loading', custListError: '' });
    try {
      const page = await customersApi.list(custQuery(get()));
      // A slower answer to an older question is not this list.
      if (mine !== custRequest) return;
      set({
        custList: page.rows,
        // The server clamps a page past the end and a size it will not serve,
        // so these are what the page actually was — never what was asked for.
        custPage: page.page,
        custPages: page.pageCount,
        custSize: page.pageSize,
        custTotal: page.total,
        custVisible: page.totalVisible,
        custListStatus: 'ready',
        custListError: '',
      });
    } catch (err) {
      if (mine !== custRequest) return;
      set({
        custListStatus: 'error',
        custListError:
          err instanceof ApiError
            ? err.message
            : 'Could not load the customers. Check the wifi and try again.',
      });
    }
  },

  sortCustomersBy: (column) => {
    const st = get();
    // Turning the column you are already in order of is what a second click
    // means. A new column starts where that column is most useful: names count
    // up, and the next promise opens on the one that is closest.
    const dir: SortDirection =
      st.custSort === column ? (st.custDir === 'asc' ? 'desc' : 'asc') : 'asc';
    // Back to page 1: page 4 of one ordering is nowhere in particular in
    // another, and landing there would read as customers having gone missing.
    set({ custSort: column, custDir: dir, custPage: 1 });
    void get().loadCustomerList();
  },

  goToCustomerPage: (page) => {
    set({ custPage: Math.max(1, page) });
    void get().loadCustomerList();
  },

  setCustomerPageSize: (size) => {
    set({ custSize: size, custPage: 1 });
    void get().loadCustomerList();
  },

  setCustomerQuery: (q) => {
    set({ custQ: q, custPage: 1 });
    // What is on screen is what was typed, the moment it was typed; only the
    // asking waits, because the shop's wifi is what a request per letter costs.
    clearTimeout(custSearchTimer);
    custSearchTimer = setTimeout(() => void get().loadCustomerList(), SEARCH_MS);
  },

  addSite: async (customer, site) => {
    try {
      await customersApi.update(customer.id, { sites: customer.sites.concat([site]) });
      await get().loadCustomers();
    } catch (err) {
      // The job is already made; only the address failed to stick. Say which,
      // rather than letting the customer page quietly not have it.
      get().flash(
        err instanceof ApiError
          ? `${customer.name} did not keep the new site: ${err.message}`
          : `Could not add ${site} to ${customer.name}. Add it from their page.`,
      );
    }
  },

  openNewCustomer: () =>
    set({
      custOpen: true,
      custEditId: null,
      custDraft: { ...EMPTY_CUSTOMER },
      custError: '',
      dialogBase: JSON.stringify(EMPTY_CUSTOMER),
    }),

  openEditCustomer: (customer) => {
    const custDraft: CustomerDraft = {
      name: customer.name,
      type: customer.type,
      contact: customer.contact ?? '',
      phone: customer.phone ?? '',
      email: customer.email ?? '',
      ref: customer.ref ?? '',
      sites: customer.sites.join('\n'),
      note: customer.note,
    };
    set({
      custOpen: true,
      custEditId: customer.id,
      custError: '',
      custDraft,
      dialogBase: JSON.stringify(custDraft),
    });
  },

  setCustDraft: (patch) => set({ custDraft: { ...get().custDraft, ...patch }, custError: '' }),

  /** Add a customer, or save changes to one.
   *
   *  The duplicate check runs here before the request as well as on the server
   *  after it — not because this answer counts, but because the office should be
   *  told they already have this customer while they are still looking at the
   *  form, and told which one. The server refuses the write either way, and the
   *  unique indexes sit under that. */
  saveCustomer: async () => {
    const st = get();
    if (st.savingCust) return;
    const d = st.custDraft;
    const editing = st.custEditId;
    const name = d.name.trim();
    if (!name) return set({ custError: 'Give the customer a name.' });

    const clash = findDuplicates(st.custs, { name, email: d.email, phone: d.phone }, editing)[0];
    if (clash) {
      return set({
        custError: `${duplicateWarning(clash)} Open them and add the work there, rather than a second record for the same customer.`,
      });
    }

    // Every field goes, blanks included: a blank clears, which is how a wrong
    // number gets taken off a customer rather than living there forever.
    const body = {
      name,
      type: d.type,
      contact: d.contact.trim(),
      phone: d.phone.trim(),
      email: d.email.trim(),
      ref: d.ref.trim(),
      sites: d.sites
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
      note: d.note.trim(),
    };

    set({ savingCust: true, custError: '' });
    try {
      const saved = editing
        ? await customersApi.update(editing, body)
        : await customersApi.create(body);

      // A job points at its customer now rather than naming one, so a rename
      // carries to the board on its own — but the name is denormalised onto
      // the answer, so the board is asked again for it. This is the bridge
      // that re-keyed the fixture jobs, landed.
      const before = editing ? st.custs.find((c) => c.id === editing)?.name : undefined;
      if (before && before !== saved.name) void get().loadJobs();

      set({
        savingCust: false,
        custOpen: false,
        custEditId: null,
        custError: '',
        screen: 'customer',
        custName: saved.name,
      });

      // Re-read rather than splicing the change in: the server decides the
      // order, and one place deciding it is enough. Both lists — the whole one
      // the form and the duplicate check read, and the page the customers
      // screen is drawing behind this dialog.
      void get().loadCustomerList();
      await get().loadCustomers();

      st.flash(
        editing ? `${saved.name} saved.` : `${saved.name} added. Their jobs will collect here.`,
      );
    } catch (err) {
      // The form stays open with what they typed: a duplicate is fixable in
      // place, and retyping the rest is not a punishment anyone deserves.
      set({
        savingCust: false,
        custError: err instanceof ApiError ? err.message : 'Could not save. Try again in a moment.',
      });
    }
  },

  /* — the dashboard and the shop's notice board — */

  /** Ask for the dashboard. The screen does this when it opens, and again
   *  whenever the board's stage flags change under it, because "in the shop"
   *  is one of the things being asked. */
  loadDashboard: async () => {
    const st = get();
    if (st.dashStatus === 'loading') return;
    set({ dashStatus: 'loading', dashError: '' });
    try {
      // What counts as still in the shop is resolved here from the stage flags
      // and sent as ids — the same bargain the customers list makes, and it
      // goes with that one now the server has the stages to read.
      const dash = await dashboardApi.get({
        liveStages: get()
          .stages.filter((x) => !x.terminal)
          .map((x) => x.id),
      });
      set({ dash, dashStatus: 'ready', dashError: '' });
    } catch (err) {
      set({
        dashStatus: 'error',
        dashError:
          err instanceof ApiError
            ? err.message
            : 'Could not load the dashboard. Check the wifi and try again.',
      });
    }
  },

  /** Read the board. Asked for on the way in, because the count rides on the
   *  Dashboard tab from wherever you are. */
  loadNotices: async () => {
    set({ noticesStatus: 'loading', noticesError: '' });
    try {
      const { notices, unread } = await noticesApi.board();
      set({ notices, unread, noticesStatus: 'ready', noticesError: '' });
    } catch (err) {
      set({
        noticesStatus: 'error',
        noticesError:
          err instanceof ApiError
            ? err.message
            : 'Could not load the notice board. Check the wifi and try again.',
      });
    }
  },

  /** Mark what is on screen as seen.
   *
   *  The ids are the notices actually drawn, never "everything": one put up
   *  while the dashboard sat open on a bench has not been read by whoever left
   *  it there, and must still be counted when they next look.
   *
   *  A failure is silent on purpose. Nothing the shop asked for has failed —
   *  they read the notices, which is the part that mattered — and a toast about
   *  it would be the app complaining about its own bookkeeping. The count stays
   *  up and clears next time. */
  markNoticesRead: async () => {
    const unread = get().notices.filter((n) => !n.read);
    if (unread.length === 0) return;
    try {
      const board = await noticesApi.markRead({ ids: unread.map((n) => n.id) });
      set({ notices: board.notices, unread: board.unread });
    } catch {
      // The badge clears on the next load. Nothing else is owed here.
    }
  },

  openNewNotice: () =>
    set({
      noticeOpen: true,
      noticeEditId: null,
      noticeDraft: { ...EMPTY_NOTICE },
      noticeError: '',
      dialogBase: JSON.stringify(EMPTY_NOTICE),
    }),

  openEditNotice: (notice) => {
    const noticeDraft: NoticeDraft = {
      title: notice.title,
      body: notice.body,
      level: notice.level,
    };
    set({
      noticeOpen: true,
      noticeEditId: notice.id,
      noticeDraft,
      noticeError: '',
      dialogBase: JSON.stringify(noticeDraft),
    });
  },

  setNoticeDraft: (patch) =>
    set({ noticeDraft: { ...get().noticeDraft, ...patch }, noticeError: '' }),

  /** Put a notice up, or correct one already up.
   *
   *  Correcting does not make it new again: the shop has read what it read, and
   *  marking everybody unread over a fixed typo is how a count stops meaning
   *  anything. The server is what holds that line — nothing here has to. */
  saveNotice: async () => {
    const st = get();
    if (st.savingNotice) return;
    if (!isOffice(st)) return st.flash(OFFICE_ONLY_NOTICES);

    const d = st.noticeDraft;
    const title = d.title.trim();
    if (!title) {
      return set({ noticeError: 'Say what the notice is — it is the line the shop reads.' });
    }
    const editing = st.noticeEditId;
    const body = { title, body: d.body.trim(), level: d.level };

    set({ savingNotice: true, noticeError: '' });
    try {
      if (editing) await noticesApi.update(editing, body);
      else await noticesApi.create(body);

      set({ savingNotice: false, noticeOpen: false, noticeEditId: null, noticeError: '' });
      // Re-read rather than splicing it in: the server decides the order and
      // what counts as read, and one place deciding it is enough.
      await get().loadNotices();
      get().flash(editing ? 'Notice saved' : 'Notice put up');
    } catch (err) {
      set({
        savingNotice: false,
        noticeError:
          err instanceof ApiError
            ? err.message
            : 'Could not put the notice up. Check the wifi and try again.',
      });
    }
  },

  /** Take a notice down. It goes, and everyone's read of it goes with it — a
   *  board is what is up now, not a filing cabinet. */
  takeNoticeDown: async (id) => {
    if (!isOffice(get())) return get().flash(OFFICE_ONLY_NOTICES);
    try {
      await noticesApi.remove(id);
      set({ noticeOpen: false, noticeEditId: null, noticeError: '' });
      await get().loadNotices();
      get().flash('Notice taken down');
    } catch (err) {
      get().flash(
        err instanceof ApiError
          ? err.message
          : 'Could not take the notice down. Check the wifi and try again.',
      );
    }
  },

  loadPeople: async () => {
    if (get().peopleStatus === 'loading') return;
    set({ peopleStatus: 'loading', peopleError: '' });
    try {
      set({ people: await peopleApi.list(), peopleStatus: 'ready', peopleError: '' });
    } catch (err) {
      set({
        peopleStatus: 'error',
        peopleError:
          err instanceof ApiError
            ? err.message
            : 'Could not load the people list. Check the wifi and try again.',
      });
    }
  },

  openAddPerson: () =>
    set({
      personOpen: true,
      personEditId: null,
      personDraft: { ...EMPTY_PERSON },
      personError: '',
      dialogBase: JSON.stringify(EMPTY_PERSON),
    }),

  openEditPerson: (person) => {
    // The password is not in play here: it is theirs, and the office resetting
    // it is a different job from fixing a role.
    const personDraft: PersonDraft = {
      name: person.user.name,
      email: person.user.email,
      password: '',
      role: person.role,
      financialAccess: person.financialAccess,
    };
    set({
      personOpen: true,
      personEditId: person.id,
      personError: '',
      personDraft,
      dialogBase: JSON.stringify(personDraft),
    });
  },

  setPersonDraft: (patch) => {
    const draft = { ...get().personDraft, ...patch };
    // Subcontractors never get financial access (IDEA.md §5). The server drops
    // it either way; the form must not leave a ticked toggle that gets ignored.
    if (draft.role === 'subcontractor') draft.financialAccess = false;
    set({ personDraft: draft, personError: '' });
  },

  savePerson: async () => {
    const st = get();
    if (st.savingPerson) return;
    const d = st.personDraft;
    const editing = st.personEditId;
    set({ savingPerson: true, personError: '' });

    try {
      const name = d.name.trim();
      const done = editing
        ? await peopleApi.update(editing, {
            name,
            role: d.role,
            financialAccess: d.financialAccess,
          })
        : (
            await peopleApi.create({
              name,
              email: d.email.trim(),
              password: d.password,
              role: d.role,
              financialAccess: d.financialAccess,
            })
          ).person;

      set({ savingPerson: false, personOpen: false, personEditId: null, personError: '' });

      // Re-read the list rather than splicing the change in: the server decides
      // the order, and one place deciding it is enough.
      await get().loadPeople();

      st.flash(
        editing
          ? `${done.user.name} saved — ${ROLE_LABEL[done.role]}, ${done.financialAccess ? 'with' : 'without'} money access.`
          : `${done.user.name} added as ${ROLE_LABEL[done.role]} — give them the password you set.`,
      );
    } catch (err) {
      // The dialog stays open with what they typed: a 409 on the email is
      // fixable in place, and retyping the rest of the form is not a punishment
      // anyone deserves.
      set({
        savingPerson: false,
        personError:
          err instanceof ApiError ? err.message : 'Could not save. Try again in a moment.',
      });
    }
  },

  openResetPassword: (person) => set({ resetId: person.id, resetDraft: '', resetError: '' }),

  setResetPassword: (value) => set({ resetDraft: value, resetError: '' }),

  resetPassword: async () => {
    const st = get();
    const id = st.resetId;
    if (!id || st.savingReset) return;

    // Say the rule before the round trip: it is the same constant the server
    // enforces, so the two cannot drift apart.
    if (st.resetDraft.length < PASSWORD_MIN_LENGTH) return set({ resetError: PASSWORD_RULE });

    set({ savingReset: true, resetError: '' });
    try {
      const person = await peopleApi.resetPassword(id, st.resetDraft);
      set({ savingReset: false, resetId: null, resetDraft: '', resetError: '' });
      await get().loadPeople();
      st.flash(
        `${person.user.name}'s password is set — tell them. They are signed out everywhere until they use it.`,
      );
    } catch (err) {
      set({
        savingReset: false,
        resetError:
          err instanceof ApiError ? err.message : 'Could not set it. Try again in a moment.',
      });
    }
  },

  openChangePassword: () =>
    set({ pwOpen: true, pwCurrent: '', pwNext: '', pwError: '', menuOpen: false }),

  setPassword: (patch) =>
    set({
      pwCurrent: patch.current ?? get().pwCurrent,
      pwNext: patch.next ?? get().pwNext,
      pwError: '',
    }),

  changePassword: async () => {
    const st = get();
    if (st.savingPw) return;
    set({ savingPw: true, pwError: '' });

    try {
      await authApi.changePassword({ currentPassword: st.pwCurrent, newPassword: st.pwNext });

      // Changing a password signs you out everywhere, and this session is one
      // of the everywheres — the server has already cleared the cookies. So the
      // only honest thing to draw is the sign-in screen, saying why.
      clearTimeout(toastTimer);
      set({
        savingPw: false,
        pwOpen: false,
        pwCurrent: '',
        pwNext: '',
        authStatus: 'signed-out',
        session: null,
        menuOpen: false,
        toast: '',
        people: [],
        peopleStatus: 'idle',
        authError: '',
        authNotice: 'Your password is changed. Sign in with the new one.',
      });
    } catch (err) {
      set({
        savingPw: false,
        pwError:
          err instanceof ApiError ? err.message : 'Could not change it. Try again in a moment.',
      });
    }
  },

  setPersonStatus: async (status) => {
    const st = get();
    const id = st.personEditId;
    if (!id || st.savingPerson) return;
    set({ savingPerson: true, personError: '' });

    try {
      const person = await peopleApi.update(id, { status });
      set({ savingPerson: false, personOpen: false, personEditId: null, personError: '' });
      await get().loadPeople();

      st.flash(
        status === 'deactivated'
          ? `${person.user.name} deactivated — they cannot sign in, and everything they did stays on the jobs.`
          : `${person.user.name} reactivated — same email, same password.`,
      );
    } catch (err) {
      set({
        savingPerson: false,
        personError:
          err instanceof ApiError ? err.message : 'Could not save. Try again in a moment.',
      });
    }
  },
}));

/** What the next job code will be, going on what is on the board.
 *
 *  A preview for the new-job dialog's title, not the answer: the server makes
 *  the code, because two people taking a truck in at the same moment must not
 *  both be handed J-1063. The toast after it is created names the real one. */
export function nextCode(jobs: Job[]): string {
  const n = jobs.reduce((m, j) => Math.max(m, parseInt(j.code.replace(/\D/g, ''), 10) || 0), 0);
  return `J-${n + 1}`;
}

/** Finish name for a sheen index. */
export const sheenName = (i: number) => SHEENS[i];
